import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import html2canvas from "html2canvas";
import { supabase, dbToDeal, upsertProperty, loadAssignedComps, loadRentRoll, assignCompsToDeal, unassignCompFromDeal, loadMapBoundaries, saveMapBoundary, deleteMapBoundary } from "../../supabase";
import { MAP_LAYER_COLORS, MAP_LAYER_LABELS, MAP_LAYER_LETTERS, MUTED_STAGE_KEYS, lookupSubAddr, normalizeMapStage } from "../../constants/mapLayers";
import { geocodeAddress, geocodeForView, _geocodeDelay } from "../../services/geocoding";
import { parseDealName } from "../../utils/parseDealName";
import FocusedDealCompTables from "./FocusedDealCompTables";
import MapListView from "./MapListView";
import MapToolRail from "./MapToolRail";
import MapLocationPanel from "./MapLocationPanel";
import MapFiltersPanel from "./MapFiltersPanel";
import PropertyPage from "./PropertyPage";
import MapResultCards from "./MapResultCards";
import { buildIsochrone, estimateElements } from "../../services/isochrone";
import {
  pinPassesLocation, pinPassesAttrFilters, locationOptions,
  activeAttrCount, activeLocationCount, EMPTY_ATTR_FILTERS,
} from "../../utils/mapFilters";
import { pointInPolygon, polygonBounds, polygonAreaSqMi } from "../../utils/geometry";
import { addressKey } from "../../utils/addressKey";

// ── + Add Deal picker modal (Phase 4) ─────────────────────────────────
// Lets the user add another deal to the focused set, or — when at the
// MAX_FOCUSED_DEALS cap — swap which deal occupies a slot.
function AddDealPicker({ open, onClose, focusedDealIds, availableDeals, onAdd, focusedDealsData, S, darkMode }) {
  const [q, setQ] = useState("");
  const [replaceTarget, setReplaceTarget] = useState(null);
  useEffect(() => { if (open) { setQ(""); setReplaceTarget(focusedDealIds.length >= MAX_FOCUSED_DEALS ? focusedDealIds[focusedDealIds.length - 1] : null); } }, [open, focusedDealIds]);
  if (!open) return null;
  const atCap = focusedDealIds.length >= MAX_FOCUSED_DEALS;
  const focusedSet = new Set(focusedDealIds);
  const qLc = q.trim().toLowerCase();
  const rows = (availableDeals || [])
    .filter(d => !focusedSet.has(d.id))
    .filter(d => {
      if (!qLc) return true;
      const name = (d._dealName || d.sourceNotes?.match(/^Name:\s*(.+)$/m)?.[1] || d.addresses?.[0] || "").toLowerCase();
      const addr = (d.addresses?.join(" ") || "").toLowerCase();
      const market = (d.market || "").toLowerCase();
      return name.includes(qLc) || addr.includes(qLc) || market.includes(qLc);
    })
    .slice(0, 100);

  return (
    <div onClick={onClose} style={{ position: "absolute", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 30 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 540, maxHeight: "80%", background: S.sidebar, color: S.text, borderRadius: 12, display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.5)", overflow: "hidden", fontFamily: S.font }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800 }}>{atCap ? "Swap a focused deal" : "Add deal to compare"}</div>
            <div style={{ fontSize: 10, color: S.muted, marginTop: 2 }}>
              {atCap
                ? `Cap is ${MAX_FOCUSED_DEALS}. Pick a slot to replace, then pick the new deal.`
                : `${focusedDealIds.length}/${MAX_FOCUSED_DEALS} slots used.`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: 17, padding: 4 }}>✕</button>
        </div>

        {atCap && (
          <div style={{ padding: "8px 16px", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, color: S.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Replace:</span>
            {focusedDealIds.map((did, idx) => {
              const c = DEAL_FOCUS_COLORS[idx] || "#888";
              const n = focusedDealsData.get(did)?.name || "deal";
              const active = replaceTarget === did;
              return (
                <button key={did} onClick={() => setReplaceTarget(did)}
                  style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, border: `1px solid ${active ? c : S.border}`, background: active ? `${c}1f` : "transparent", cursor: "pointer" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: c }} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: active ? c : S.text }}>{n}</span>
                </button>
              );
            })}
          </div>
        )}

        <div style={{ padding: "8px 16px", borderBottom: `1px solid ${S.border}` }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search deals…" autoFocus
            style={{ width: "100%", boxSizing: "border-box", background: S.card, border: `1px solid ${S.border}`, borderRadius: 6, padding: "6px 9px", fontSize: 11, color: S.text, fontFamily: S.font, outline: "none" }} />
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {rows.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: S.muted, fontSize: 11 }}>
              {q ? "No matching deals." : "No other deals available."}
            </div>
          )}
          {rows.map(d => {
            const name = d._dealName || d.sourceNotes?.match(/^Name:\s*(.+)$/m)?.[1] || d.addresses?.[0] || d.id;
            const subAddr = d.addresses?.[0] && name !== d.addresses[0] ? d.addresses[0] : null;
            return (
              <button
                key={d.id}
                onClick={() => onAdd(d.id, replaceTarget)}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 16px", borderBottom: `1px solid ${S.border}`, background: "transparent", border: "none", borderTop: "none", borderLeft: "none", borderRight: "none", cursor: "pointer", color: S.text, fontFamily: S.font }}
                onMouseEnter={e => e.currentTarget.style.background = S.card}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div style={{ fontSize: 12, fontWeight: 700 }}>{name}</div>
                {subAddr && <div style={{ fontSize: 10, color: S.muted, marginTop: 2 }}>{subAddr}</div>}
                <div style={{ fontSize: 9, color: S.muted, marginTop: 2 }}>{d.market || ""}{d.stage ? ` · ${d.stage}` : ""}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Helpers used by the Present-mode Deal Summary overlay card.
function avgOf(nums) {
  const valid = (nums || []).filter(n => Number.isFinite(n) && n !== 0);
  return valid.length > 0 ? valid.reduce((s, n) => s + n, 0) / valid.length : null;
}
function fmtBig(n) {
  if (n == null) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}
function KV({ k, v, S, accent = false }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 800, color: S.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>{k}</div>
      <div style={{ fontSize: 13, fontWeight: 800, color: accent ? S.red : S.text, marginTop: 1 }}>{v}</div>
    </div>
  );
}

// Small summary chip used inside the rent-roll + comp-summary drawer panels.
function SummaryStat({ label, value, S, accent = false }) {
  return (
    <div style={{ flex: 1, minWidth: 80, padding: "6px 10px", background: S.card, border: `1px solid ${S.border}`, borderRadius: 6 }}>
      <div style={{ fontSize: 8, color: S.muted, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 800, color: accent ? S.red : S.text, marginTop: 2 }}>{value}</div>
    </div>
  );
}

// Haversine distance between two lat/lng pairs, in miles.
function distMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Multi-deal compare color palette (Phase 4). Slot index → color for the
// chip + the comp pin border ring. Capped at 3 deals; a 4th add prompts the
// user to replace one of the existing slots.
const DEAL_FOCUS_COLORS = ["#e93529", "#8a8a8a", "#4f9d76"];
const MAX_FOCUSED_DEALS = 3;

function MapView({
  darkMode,
  cacheRef,
  onNavigateToDeal,
  focusTarget,
  onFocusConsumed,
  focusedDealIds = [],        // Phase 4: ordered ids of deals to compare
  onChangeFocusedDealIds,     // (ids[]) => void
  availableDeals = [],        // for the + Add deal picker
  requestPresent = false,     // Phase 5.1: deal-page 🎬 Present click sets this true so MapView mounts straight into Present mode
  onPresentRequestConsumed,   // () => void — clears the App's request flag once we've applied it
  userEmail = null,           // signed-in Entra account — stamped on saved boundaries
}) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef  = useRef(null);
  const clusterGroupsRef = useRef({});
  const markersRef      = useRef([]);
  const basemapRef      = useRef({});
  const pinnedMarkerRef = useRef(null);

  const [properties,   setProperties]   = useState([]);
  // Raw comps rows kept in state so the drawer can list every comp at a
  // clicked address (one address often has multiple lease comps for different
  // tenants/suites — the marker only represents one but we surface them all).
  const [rawComps,     setRawComps]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [compsModalOpen, setCompsModalOpen] = useState(false);
  const [layers,       setLayers]       = useState({
    // Default view is the working pipeline: active stages plus comps.
    // Tracking, the terminal stages and Outreach are all off — they are
    // large, mostly-static populations you switch on deliberately rather
    // than wade through every time the map opens.
    screening: true, underwriting: true, loi: true, contract: true,
    tracking: false, pass: false, lost: false, dead: false,
    outreach: true,
    sourcing: false,
    comp_sale: true, comp_lease: true,
  });
  const [basemap,      setBasemapState] = useState("satellite");
  const [search,       setSearch]       = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchExtResults, setSearchExtResults] = useState([]);
  const searchDebounceRef = useRef(null);
  const [geocoding,    setGeocoding]    = useState({ done: 0, total: 0 });
  const [parcelMode,   setParcelMode]   = useState(false);
  const [streetViewMode, setStreetViewMode] = useState(false);
  const [streetViewCoords, setStreetViewCoords] = useState(null);
  const streetViewRef = useRef(null);
  const streetViewPanoRef = useRef(null);
  const streetViewModeRef = useRef(false);
  const [parcelInfo,   setParcelInfo]   = useState(null);
  const [addingSourcing, setAddingSourcing] = useState(false);
  const [toast,        setToast]        = useState(null);
  const parcelLayerRef = useRef(null);
  // Ref to the .map-area DIV so html2canvas can capture the visible map + overlays.
  const mapAreaRef = useRef(null);
  // UI state while a snapshot is being captured.
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const parcelModeRef  = useRef(false);

  // ── Radius search state (CoStar-style) ─────────────────────────────────
  // Center is the geocoded result; the slider controls how wide the search
  // is. When radiusCenter is set, all property markers (and the comps drawer)
  // are filtered to within radiusMiles of the center, and a red circle is
  // drawn on the map to show the search area.
  const [radiusInput, setRadiusInput]       = useState("");
  const [radiusCenter, setRadiusCenter]     = useState(null); // { lat, lng, label }
  const [radiusMiles, setRadiusMiles]       = useState(3);
  const [radiusGeocoding, setRadiusGeocoding] = useState(false);
  const [radiusDrawMode, setRadiusDrawMode] = useState(false);
  const radiusCircleRef = useRef(null);
  const radiusDrawModeRef = useRef(false);

  // ── Boundary filter state ──────────────────────────────────────────────
  // A boundary is a single polygon ring of [lat, lng] pairs. While drawing,
  // clicks accumulate into boundaryDraft; committing moves the ring into
  // `boundary`, which every pin is then tested against (everything outside
  // is hidden, across all layers). Boundaries can be named and saved so the
  // team can reuse their own submarket definitions.
  const [boundaryDrawMode, setBoundaryDrawMode] = useState(false);
  const [boundaryDraft, setBoundaryDraft]       = useState([]);   // [[lat,lng], ...]
  const [boundary, setBoundary]                 = useState(null); // committed ring
  const [boundaryLabel, setBoundaryLabel]       = useState("");   // name, when saved/loaded
  const [savedBoundaries, setSavedBoundaries]   = useState([]);
  const [boundaryStoreReady, setBoundaryStoreReady] = useState(true);
  const [boundaryNameInput, setBoundaryNameInput]   = useState("");
  const [boundarySaving, setBoundarySaving]     = useState(false);
  const boundaryDrawModeRef = useRef(false);
  const boundaryDraftRef    = useRef([]);
  const boundaryLayerRef    = useRef(null);
  const boundaryDraftLayerRef = useRef(null);

  // ── Map / List view ────────────────────────────────────────────────────
  // Opens on "map" every time. visiblePins is filled by the marker-sync
  // effect with the exact props it drew, so the list can never drift from
  // what's on the map.
  const [mapView, setMapView]         = useState("map"); // "map" | "list"

  // Location is geography (market / submarket / city); Filters is attributes.
  // Both apply in the marker filter chain alongside boundary and radius.
  const [locSel, setLocSel] = useState(() => ({
    market: new Set(), submarket: new Set(), city: new Set(),
  }));
  const [attrFilters, setAttrFilters] = useState(() => ({ ...EMPTY_ATTR_FILTERS }));
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Property page, keyed by address. An address is the durable identity —
  // property today, deal tomorrow, comp after it trades — so the page follows
  // the building, not whichever record happened to be clicked.
  const [propertyAddrKey, setPropertyAddrKey] = useState(null);

  // Result cards beside the map.
  const [cardsOpen, setCardsOpen] = useState(true);

  // Drive-time isochrone. The ring feeds the same pointInPolygon filter
  // the drawn boundary uses.
  const [travelRing, setTravelRing]         = useState(null);
  const [travelOrigin, setTravelOrigin]     = useState(null);
  const [travelMinutes, setTravelMinutes]   = useState(15);
  const [travelBusy, setTravelBusy]         = useState(false);
  const [travelMeta, setTravelMeta]         = useState(null);
  const [travelDrawMode, setTravelDrawMode] = useState(false);
  const travelDrawModeRef = useRef(false);
  const travelLayerRef = useRef(null);
  const [visiblePins, setVisiblePins] = useState([]);

  // What the map is actually looking at. visiblePins is everything passing the
  // FILTERS; this is the window on top of it. Without it the panel said
  // "1,326 on the map" whatever you did - zoom into one building and it still
  // claimed 1,326, which made the list useless for reading the area in front
  // of you. Null until the map first settles, and null means "no window yet"
  // rather than an empty one, so nothing vanishes during startup.
  const [mapBounds, setMapBounds] = useState(null);


  // ── Deal-focus mode (Phase 4: multi-deal) ──────────────────────────
  // focusedDealIds (from props) is the single source of truth for which
  // deals are focused. Below we keep a per-deal map of loaded comp + rent
  // roll data, plus a derived "primary deal" (first in list) so existing
  // single-deal code paths continue to work.
  const [focusedDealsData, setFocusedDealsData] = useState(new Map()); // dealId → { comps, rentRoll, center, name }
  // Drawer "active deal" selector — when multiple deals are focused, the
  // drawer's Details panel shows one deal's comp summary at a time. User
  // can switch via the small chip row at the top of the drawer.
  const [drawerDealId, setDrawerDealId] = useState(null);
  // Which deal is the bulk-select Apply targeting? Defaults to the first
  // focused deal; user can swap via dropdown in the action bar when there
  // are 2+ focused deals.
  const [bulkApplyDealId, setBulkApplyDealId] = useState(null);
  // Modal state for the "+ Add deal" picker + 4th-deal replace prompt.
  const [addDealPickerOpen, setAddDealPickerOpen] = useState(false);

  // ── Present mode (Phase 5.1) ─────────────────────────────────────────
  // When on, the map goes full-bleed: sidebar, top bar, top-left stat
  // chips and the top-right Street/Parcel buttons all hide.
  // A 🎬 Exit Present floating button stays so it's obvious how to leave,
  // plus a Deal Summary overlay top-right with the drawer-active deal's
  // numbers for IC audiences.
  const [presentMode, setPresentMode] = useState(false);
  // Honor an external "enter Present mode" request from the deal page's
  // 🎬 Present button. Runs once when the request flag flips true; clears
  // the request via the callback so it doesn't fire again on every render.
  useEffect(() => {
    if (!requestPresent) return;
    setPresentMode(true);
    onPresentRequestConsumed?.();
  }, [requestPresent, onPresentRequestConsumed]);
  // Auto-exit Present mode if focus is cleared (no deals = nothing to
  // present). Prevents a stale Present overlay sitting on a blank map.
  useEffect(() => {
    if (presentMode && focusedDealIds.length === 0) setPresentMode(false);
  }, [presentMode, focusedDealIds.length]);

  // ESC closes the Comp Tables fullscreen modal.
  useEffect(() => {
    if (!compsModalOpen) return;
    const onKey = e => { if (e.key === "Escape") setCompsModalOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [compsModalOpen]);

  // Close the modal if the user un-focuses every deal (nothing to show).
  useEffect(() => {
    if (compsModalOpen && focusedDealIds.length === 0) setCompsModalOpen(false);
  }, [compsModalOpen, focusedDealIds.length]);

  // Derived: primary focused deal id (first in list). Used wherever the
  // existing single-deal code paths still expect one focused deal.
  const dealFocusId = focusedDealIds[0] || null;
  // Derived: union of every focused deal's assigned-comp ids — used by the
  // marker filter and styling to decide "assigned vs faded nearby".
  const focusedAssignedCompIds = useMemo(() => {
    const u = new Set();
    for (const data of focusedDealsData.values()) {
      for (const c of (data.comps || [])) u.add(c.id);
    }
    return u;
  }, [focusedDealsData]);
  // Derived: union of all rent roll rows (with their dealId attached for
  // the per-deal 📋 pin grouping in the rent-roll render effect).
  const focusedRentRoll = useMemo(() => {
    const all = [];
    for (const [dealId, data] of focusedDealsData) {
      for (const r of (data.rentRoll || [])) all.push({ ...r, _dealId: dealId });
    }
    return all;
  }, [focusedDealsData]);
  // For the comp-summary card the drawer-active deal's comps are needed.
  // Toggle: 'with_nearby' shows assigned comps PLUS faded nearby unassigned
  // ones within 50mi (per D4 default); 'only_assigned' hides the faded set.
  const [focusMode, setFocusMode] = useState("with_nearby"); // 'with_nearby' | 'only_assigned'
  // Selection mode for bulk add/remove. When on, clicking a comp pin
  // toggles its selected state instead of opening the popup. Cmd/Ctrl+click
  // also toggles selection even when mode is off (power-user shortcut).
  const [selectionMode, setSelectionMode] = useState(false);
  const selectionModeRef = useRef(false);
  // Comp ids currently selected in selection mode. Apply uses this set to
  // batch assign / unassign + fire a single toast.
  const [selectedCompIds, setSelectedCompIds] = useState(new Set());
  // Which subject address's rent roll panel is open in the drawer (set when
  // user clicks a 📋 subject pin). null = no rent-roll panel showing.
  const [rentRollPanelAddress, setRentRollPanelAddress] = useState(null);
  // Layer for the rent roll 📋 pins so we can swap them on focus change
  // without touching the main cluster groups.
  const rentRollLayerRef = useRef(null);

  // ── Helper: fetch all rows from a Supabase table (handles >1000 rows) ──
  async function fetchAllRows(table, options = {}) {
    const PAGE = 1000;
    let allData = [];
    let from = 0;
    while (true) {
      let q = supabase.from(table).select("*").range(from, from + PAGE - 1);
      if (options.order) q = q.order(options.order.col, { ascending: options.order.asc });
      const { data, error } = await q;
      if (error) throw error;
      allData = allData.concat(data || []);
      if (!data || data.length < PAGE) break;
      from += PAGE;
    }
    return allData;
  }

  // ── Load data from Supabase ─────────────────────────────────────────────
  useEffect(() => {
    // If we have cached data, restore instantly (but re-derive layerKeys in case stages changed)
    if (cacheRef && cacheRef.current) {
      const cached = cacheRef.current.map(p => {
        if (p.isPipeline) {
          const lk = normalizeMapStage(p.stage, p.dealFlow);
          return { ...p, layerKey: lk, color: MAP_LAYER_COLORS[lk] || "#6a6a6a" };
        }
        return p;
      });
      setProperties(cached);
      setLoading(false);
      setGeocoding({ done: 0, total: 0 });
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        // Sourcing properties are no longer drawn: the Outreach deal stage
        // already represents that population, so pinning both put the same
        // building on the map twice. Dropping them also drops five table
        // fetches — properties, contacts, outreach_log, institutional_owners
        // and app_settings — from every map load.
        const [deals, compsAll] = await Promise.all([
          fetchAllRows("deals"),
          fetchAllRows("comps"),
        ]);

        // Pipeline deals
        const pipelineProps = deals.flatMap(row => {
          const d = dbToDeal(row);
          const addresses = d.addresses && d.addresses.length > 0 ? d.addresses : [""];
          const layerKey = normalizeMapStage(d.stage, d.dealFlow);
          const dealName = parseDealName(d.sourceNotes);
          return addresses.map((addr, idx) => ({
            id: d.id + (idx > 0 ? `_addr${idx}` : ""),
            dealId: d.id,
            address: addr, market: d.market || "", stage: d.stage || "", dealFlow: d.dealFlow,
            layerKey, color: MAP_LAYER_COLORS[layerKey] || "#6a6a6a",
            sf: d.squareFeet, askingPrice: d.askingPrice, capRate: d.capRate,
            sourceNotes: d.sourceNotes, thesis: d.thesis,
            openQuestions: d.openQuestions, redFlags: d.redFlags,
            underwrittenPrice: d.underwrittenPrice, leveredIrr: d.leveredIrr,
            equityMultiple: d.equityMultiple, trendedYoc: d.trendedYoc,
            untrendedYoc: d.untrendedYoc, averageCoc: d.averageCoc,
            // First address uses deal's stored lat/lng; others use pre-geocoded cache
            lat: idx === 0 ? (row.latitude || null) : (lookupSubAddr(addr)?.lat || null),
            lng: idx === 0 ? (row.longitude || null) : (lookupSubAddr(addr)?.lng || null),
            isPipeline: true, _table: "deals", _rowId: row.id,
            // Untouched Supabase row. The list view and Excel export read
            // columns straight off this, so a new DB column shows up without
            // anyone having to extend the pin builder. Reference, not a copy.
            _raw: row,
            _dealName: dealName,
            _addrIndex: idx,
            _totalAddresses: addresses.length,
          }));
        });

        // Comps — sale and lease
        const compProps = (compsAll || []).filter(c => c.latitude && c.longitude).map(c => ({
          id: 'comp_' + c.id,
          address: [c.address, c.city, c.state].filter(Boolean).join(', '),
          market: c.market || '',
          stage: c.type === 'sale' ? 'comp_sale' : 'comp_lease',
          layerKey: c.type === 'sale' ? 'comp_sale' : 'comp_lease',
          color: c.type === 'sale' ? '#c8922f' : '#b4b4b4',
          sf: c.building_sf || c.suite_sf,
          lat: c.latitude, lng: c.longitude,
          isPipeline: false, isComp: true,
          compType: c.type,
          tenant: c.tenant, buyer: c.buyer, seller: c.seller,
          lease_rate: c.lease_rate, sale_price: c.sale_price,
          price_psf: c.price_psf, cap_rate: c.cap_rate,
          comp_date: c.comp_date, source: c.source,
          property_name: c.property_name,
          // lease_type and opex_psf drive the normalized-rate column; the rest
          // of the comp's columns come along via _raw.
          lease_type: c.lease_type, opex_psf: c.opex_psf,
          _table: 'comps', _rowId: c.id,
          _raw: c,   // see _raw note on the deal pins above
        }));

        const allProps = [...pipelineProps, ...compProps];
        if (cancelled) return;
        setProperties(allProps);
        setRawComps(compsAll || []);
        setLoading(false);
        // Save to cache so tab switches don't re-geocode
        if (cacheRef) cacheRef.current = allProps;

        // ── Portfolio sub-addresses only ───────────────────────────────
        // A deal, comp or Sourcing property is geocoded ONCE, when it is
        // created, by the strict geocoder - and that pin is saved and never
        // re-derived. This loop used to re-geocode every record without a pin
        // on every map load and write the answer back to Supabase, restricted
        // to the US and nothing narrower. It no longer writes to the database
        // at all. What is left is display-only: a portfolio deal's second and
        // later addresses carry no stored coordinate, so they are geocoded here
        // (strictly) and cached in this browser under geo2_. The key changed
        // from geo_ so answers cached under the old country-only rules are not
        // reused.
        const needsGeocode = allProps.filter(p => !p.lat && p.address && p._addrIndex > 0);
        if (needsGeocode.length === 0) { setGeocoding({ done: 0, total: 0 }); return; }
        setGeocoding({ done: 0, total: needsGeocode.length });
        const hasGoogle = !!process.env.REACT_APP_GOOGLE_GEOCODE_KEY;
        let done = 0;
        for (const prop of needsGeocode) {
          if (cancelled) return;
          const coords = await geocodeAddress(prop.address);
          if (coords && coords.lat) {
            prop.lat = coords.lat;
            prop.lng = coords.lng;
            try { localStorage.setItem("geo2_" + prop.address.toLowerCase().trim(), JSON.stringify({ lat: coords.lat, lng: coords.lng })); } catch {}
          }
          done++;
          if (done % 5 === 0 || done === needsGeocode.length) {
            setGeocoding({ done, total: needsGeocode.length });
            setProperties(prev => [...prev]);
            if (cacheRef) cacheRef.current = allProps;
          }
          // Google has no rate limit; the Census fallback is also unrestricted.
          // Only delay if we ended up in a no-Google situation AND are likely
          // to hit Census's softer per-IP throttle on long batches.
          if (!hasGoogle && done < needsGeocode.length) await _geocodeDelay();
        }
      } catch (err) {
        console.error("MapView load error:", err);
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Init Leaflet map ────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current || !window.L) return;
    const L = window.L;
    const map = L.map(mapContainerRef.current, { center: [25.8, -80.2], zoom: 7, zoomControl: false });

    // Basemaps — use dark tiles in dark mode, light tiles in light mode
    const sat = L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { attribution: "© Esri", maxZoom: 19 });
    const street = darkMode
      ? L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", { attribution: "© CartoDB", maxZoom: 19 })
      : L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 });
    const hybrid = L.layerGroup([
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 19 }),
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", { maxZoom: 19 }),
    ]);
    sat.addTo(map);
    basemapRef.current = { satellite: sat, street, hybrid };

    // Per-layer cluster groups with matching colors
    Object.entries(MAP_LAYER_COLORS).forEach(([layer, color]) => {
      clusterGroupsRef.current[layer] = L.markerClusterGroup({
        showCoverageOnHover: false,
        maxClusterRadius: 40,
        disableClusteringAtZoom: 17,
        iconCreateFunction: (cluster) => {
          const n = cluster.getChildCount();
          return L.divIcon({
            html: `<div class="pp-cluster" style="background:${color}">${n}</div>`,
            className: "", iconSize: [32, 32], iconAnchor: [16, 16],
          });
        },
      });
    });

    mapInstanceRef.current = map;

    // Tie the result panel to the viewport. `moveend` covers panning, zooming
    // and fitBounds alike and fires once when movement settles, not per frame,
    // so this needs no debouncing. Stored as plain numbers because a Leaflet
    // LatLngBounds is a fresh object every call - holding one in state would
    // re-render on every pan whether or not the window actually changed.
    const syncBounds = () => {
      const b = map.getBounds();
      setMapBounds((prev) => {
        const next = { s: b.getSouth(), w: b.getWest(), n: b.getNorth(), e: b.getEast() };
        if (prev && prev.s === next.s && prev.w === next.w
            && prev.n === next.n && prev.e === next.e) return prev;
        return next;
      });
    };
    map.on("moveend", syncBounds);
    syncBounds();

    return () => { map.off("moveend", syncBounds); map.remove(); mapInstanceRef.current = null; };
  }, []);

  // ── Sync markers ────────────────────────────────────────────────────────
  useEffect(() => {
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    // Clear all cluster groups
    Object.values(clusterGroupsRef.current).forEach(g => { g.clearLayers(); if (map.hasLayer(g)) map.removeLayer(g); });
    markersRef.current = [];
    pinnedMarkerRef.current = null;
    const searchLc = search.toLowerCase();
    // Collected alongside the markers so the pin table lists exactly what got
    // drawn, with no second copy of the filter chain to drift out of sync.
    const passing = [];

    properties.forEach(prop => {
      if (!prop.lat || !prop.lng) return;
      if (!layers[prop.layerKey]) return;
      // Search filter
      if (searchLc) {
        const hay = [prop.address, prop.market, prop.stage, prop._dealName].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(searchLc)) return;
      }
      // Location panel (market / submarket / city) and Filters panel
      if (!pinPassesLocation(prop, locSel)) return;
      if (!pinPassesAttrFilters(prop, attrFilters)) return;
      // Radius filter — when active, drop anything outside the circle.
      if (radiusCenter && distMiles(radiusCenter.lat, radiusCenter.lng, prop.lat, prop.lng) > radiusMiles) return;
      // Boundary filter — when a polygon is committed, drop anything outside
      // it. Applies to every layer: deals, comps and sourcing alike.
      if (boundary && !pointInPolygon(prop.lat, prop.lng, boundary)) return;
      // Drive-time ring behaves exactly like a drawn boundary.
      if (travelRing && !pointInPolygon(prop.lat, prop.lng, travelRing)) return;
      // Deal-focus filter — focused-deal pins + assigned-or-nearby comps.
      // Multi-deal: a comp is "nearby" if within 50mi of ANY focused deal's
      // centroid (so a Texas+Florida compare keeps both metros visible).
      if (focusedDealIds.length > 0) {
        const isFocusedDealPin = prop.isPipeline && focusedDealIds.includes(prop.dealId);
        if (!isFocusedDealPin && !prop.isComp) return;
        if (prop.isComp) {
          const assigned = focusedAssignedCompIds.has(prop._rowId);
          if (focusMode === "only_assigned" && !assigned) return;
          if (!assigned) {
            // Check distance to closest focused-deal centroid
            let withinAny = false;
            for (const data of focusedDealsData.values()) {
              if (!data.center) continue;
              if (distMiles(data.center.lat, data.center.lng, prop.lat, prop.lng) <= DEAL_FOCUS_COMP_RADIUS_MI) {
                withinAny = true; break;
              }
            }
            if (!withinAny) return;
          }
        }
      }

      passing.push(prop);
    });

    // Everything drawn goes to the list ungrouped — the table wants every
    // record. The map does not: five suites in one building were five pins
    // stacked on each other.
    setVisiblePins(passing);

    // One pin per address per type. Grouping never crosses type, so colour
    // still means exactly one thing; a building with a deal, a sale comp and
    // four lease comps is three pins, not six.
    const groups = new Map();
    passing.forEach(prop => {
      // addressKey drops city/state, so a coarse lat/lng cell keeps two
      // buildings with the same street address in different markets apart.
      const cell = (prop.lat != null && prop.lng != null)
        ? `${prop.lat.toFixed(1)},${prop.lng.toFixed(1)}` : "";
      const key = `${prop.layerKey}|${addressKey(prop.address) || prop.id}|${cell}`;
      const g = groups.get(key);
      if (g) g.push(prop); else groups.set(key, [prop]);
    });

    groups.forEach(members => {
      const prop = members[0];
      const stackCount = members.length;

      const color = MAP_LAYER_COLORS[prop.layerKey] || "#6a6a6a";
      const letter = MAP_LAYER_LETTERS[prop.layerKey] || "?";
      const muted = MUTED_STAGE_KEYS.includes(prop.layerKey);
      const isSelected = prop.isComp && members.some(m => selectedCompIds.has(m._rowId));

      // Deal-focus mode still distinguishes comps assigned to a focused deal
      // from merely-nearby ones; it now does it by ring and opacity on the
      // same pin shape rather than by swapping in a different marker.
      const isFocusActive = focusedDealIds.length > 0;
      const assignedColors = (isFocusActive && prop.isComp)
        ? focusedDealIds
            .filter(did => (focusedDealsData.get(did)?.comps || [])
              .some(c => members.some(m => c.id === m._rowId)))
            .map(did => DEAL_FOCUS_COLORS[focusedDealIds.indexOf(did)])
        : [];
      const isAssigned = assignedColors.length > 0;
      const faded = isFocusActive && prop.isComp && !isAssigned;

      const cls = ["pp-pin", muted && "muted", faded && "faded", isAssigned && "assigned"]
        .filter(Boolean).join(" ");
      const ring = isAssigned ? `box-shadow:0 0 0 2px ${assignedColors[0]}` : "";

      const icon = L.divIcon({
        html: `<div class="pp-pin-wrap">`
          + `<div class="${cls}" style="background:${color};${ring}"><span>${letter}</span></div>`
          + (stackCount > 1 ? `<div class="pp-count">${stackCount}</div>` : "")
          + (isSelected ? `<div class="pp-check">\u2713</div>` : "")
          + `</div>`,
        className: "", iconSize: [26, 26], iconAnchor: [13, 26], popupAnchor: [0, -28],
      });

      const marker = L.marker([prop.lat, prop.lng], { icon });
      marker._ppProp = prop;
      marker._ppStack = members;
      const tipHtml = buildTipHtml(prop, stackCount);
      marker.bindTooltip(tipHtml, { sticky: true, className: "pp-tip" });
      marker.on("click", (e) => {
        // Bulk-select mode (or Cmd/Ctrl+click power shortcut): toggle the
        // comp's selection state instead of opening its popup. Only comps in
        // deal-focus mode are eligible — clicking a deal pin or sourcing pin
        // still behaves normally.
        const isModifier = e?.originalEvent?.metaKey || e?.originalEvent?.ctrlKey;
        if (focusedDealIds.length > 0 && prop.isComp && (selectionModeRef.current || isModifier)) {
          setSelectedCompIds(prev => {
            const next = new Set(prev);
            if (next.has(prop._rowId)) next.delete(prop._rowId); else next.add(prop._rowId);
            return next;
          });
          return;
        }
        // Unpin previous tooltip
        if (pinnedMarkerRef.current && pinnedMarkerRef.current !== marker) {
          try {
            pinnedMarkerRef.current.unbindTooltip();
            if (pinnedMarkerRef.current._ppProp) pinnedMarkerRef.current.bindTooltip(buildTipHtml(pinnedMarkerRef.current._ppProp), { sticky: true, className: "pp-tip" });
          } catch (e) {}
        }
        // Pin this tooltip
        marker.unbindTooltip();
        marker.bindTooltip(tipHtml, { permanent: true, className: "pp-tip", direction: "top", offset: [0, -10] });
        marker.openTooltip();
        pinnedMarkerRef.current = marker;
        openPropertyPage(prop);
        setRentRollPanelAddress(null); // clear the 📋 panel if it was open
        // If a focused deal pin was clicked, sync the drawer-active deal so
        // its comp summary card reflects that deal (not whichever was last
        // active when there are 2+ focused deals).
        if (prop.isPipeline && focusedDealIds.includes(prop.dealId)) {
          setDrawerDealId(prop.dealId);
          setBulkApplyDealId(prop.dealId);
        }
      });
      const group = clusterGroupsRef.current[prop.layerKey];
      if (group) { group.addLayer(marker); }
      markersRef.current.push(marker);
    });

    // Add visible cluster groups to map
    Object.entries(layers).forEach(([k, vis]) => {
      if (vis && clusterGroupsRef.current[k]) {
        clusterGroupsRef.current[k].addTo(map);
      }
    });

    // Auto-fit bounds for the default (non-focused) case only. Deal-focus
    // zoom is handled by a dedicated effect below so it can't race with
    // marker rebuilds.
    // A committed boundary owns the viewport (a dedicated effect fits to the
    // polygon), so don't also re-fit to the surviving pins and fight it.
    if (!dealFocusId && !boundary && markersRef.current.length > 0) {
      const bounds = L.latLngBounds(markersRef.current.map(m => m.getLatLng()));
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, layers, search, locSel, attrFilters, radiusCenter, radiusMiles, boundary, travelRing, dealFocusId, focusedAssignedCompIds, focusMode, selectedCompIds]);

  // ── Rent roll subject pins (📋) ─────────────────────────────────────
  // One pin per address that has rent roll data for the focused deal (per
  // F1 = B). Lat/lng comes from the matching deal pin in `properties`.
  // Click → opens the rent roll panel in the side drawer.
  useEffect(() => {
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;
    // Tear down whatever was there
    if (rentRollLayerRef.current) {
      try { map.removeLayer(rentRollLayerRef.current); } catch {}
      rentRollLayerRef.current = null;
    }
    if (focusedDealIds.length === 0 || focusedRentRoll.length === 0) return;

    // Group rent roll rows by (subject_address, dealId) so each focused deal
    // gets its own 📋 pin even if two focused deals happen to share an
    // address (rare but possible).
    const bySubject = new Map();
    for (const r of focusedRentRoll) {
      const k = `${(r.subject_address || "").trim()}|${r._dealId || ""}`;
      if (!r.subject_address) continue;
      if (!bySubject.has(k)) bySubject.set(k, { subj: r.subject_address.trim(), dealId: r._dealId, rows: [] });
      bySubject.get(k).rows.push(r);
    }
    const group = L.layerGroup();
    for (const { subj, dealId, rows } of bySubject.values()) {
      // Pull pins for the row's deal
      const dealPins = properties.filter(p => p.isPipeline && p.dealId === dealId && p.lat && p.lng);
      const subjLc = subj.toLowerCase();
      const match = dealPins.find(p => {
        const a = (p.address || "").toLowerCase();
        return a.includes(subjLc) || subjLc.includes(a.split(",")[0]?.trim() || "");
      }) || dealPins[0];
      if (!match) continue;
      // Per-deal color for the 📋 border
      const slotIdx = focusedDealIds.indexOf(dealId);
      const borderColor = slotIdx >= 0 ? DEAL_FOCUS_COLORS[slotIdx] : "#e93529";
      const totalSf = rows.reduce((s, r) => s + (Number(r.suite_sf) || 0), 0);
      const tipHtml = `<div style="font-family:'Montserrat',sans-serif;line-height:1.4"><strong style="font-size:11px">📋 Rent Roll</strong><br><span style="font-size:10px;color:#888">${rows.length} tenants · ${totalSf.toLocaleString()} SF</span><br><span style="font-size:9px;color:#aaa">${subj}</span><br><em style="font-size:9px;color:#888">Click to see details</em></div>`;
      const icon = L.divIcon({
        html: `<div style="background:#fff;width:24px;height:24px;border-radius:50%;border:2px solid ${borderColor};display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.4);font-size:13px;line-height:1">📋</div>`,
        className: "",
        iconSize: [24, 24],
        iconAnchor: [-2, 30],     // nudge to upper-right of the deal pin
        popupAnchor: [0, -10],
      });
      const m = L.marker([match.lat, match.lng], { icon, zIndexOffset: 1000 });
      m.bindTooltip(tipHtml, { sticky: true, className: "pp-tip" });
      m.on("click", () => {
        // Track which deal's rent roll we're showing (so the panel can scope
        // rows correctly when multiple deals share addresses).
        setRentRollPanelAddress(subj);
        if (dealId) setDrawerDealId(dealId);
      });
      group.addLayer(m);
    }
    group.addTo(map);
    rentRollLayerRef.current = group;
  }, [focusedDealIds, focusedRentRoll, properties, focusedDealsData]);

  // ── Radius circle overlay ─────────────────────────────────────────────
  // Draw / update / remove the search circle as the radius state changes.
  // Uses miles → meters conversion (Leaflet circles take meters).
  useEffect(() => {
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;
    if (radiusCircleRef.current) {
      map.removeLayer(radiusCircleRef.current);
      radiusCircleRef.current = null;
    }
    if (radiusCenter) {
      radiusCircleRef.current = L.circle([radiusCenter.lat, radiusCenter.lng], {
        radius: radiusMiles * 1609.344,
        color: "#e93529",
        weight: 2,
        fillColor: "#e93529",
        fillOpacity: 0.06,
        interactive: false,
      }).addTo(map);
    }
  }, [radiusCenter, radiusMiles]);

  // ── Boundary polygon overlay ──────────────────────────────────────────
  // Committed boundary: filled PP-red polygon. Kept non-interactive so it
  // never swallows clicks meant for pins underneath it.
  useEffect(() => {
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;
    if (boundaryLayerRef.current) {
      map.removeLayer(boundaryLayerRef.current);
      boundaryLayerRef.current = null;
    }
    if (boundary && boundary.length >= 3) {
      boundaryLayerRef.current = L.polygon(boundary, {
        color: "#e93529",
        weight: 2,
        fillColor: "#e93529",
        fillOpacity: 0.05,
        dashArray: "6,4",
        interactive: false,
      }).addTo(map);
    }
  }, [boundary]);

  // Draft boundary: the in-progress line plus a vertex dot per click. The
  // first vertex is drawn larger and hollow because clicking it closes the
  // shape.
  useEffect(() => {
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;
    if (boundaryDraftLayerRef.current) {
      map.removeLayer(boundaryDraftLayerRef.current);
      boundaryDraftLayerRef.current = null;
    }
    if (boundaryDrawMode && boundaryDraft.length > 0) {
      const group = L.layerGroup();
      if (boundaryDraft.length > 1) {
        L.polyline(boundaryDraft, { color: "#e93529", weight: 2, dashArray: "5,5", interactive: false }).addTo(group);
      }
      boundaryDraft.forEach((pt, i) => {
        const isFirst = i === 0;
        L.circleMarker(pt, {
          radius: isFirst ? 6 : 4,
          color: "#e93529",
          weight: 2,
          fillColor: isFirst ? "#ffffff" : "#e93529",
          fillOpacity: 1,
          interactive: false,
        }).addTo(group);
      });
      group.addTo(map);
      boundaryDraftLayerRef.current = group;
    }
  }, [boundaryDrawMode, boundaryDraft]);

  // ── Drive-time ring overlay ───────────────────────────────────────────
  useEffect(() => {
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;
    if (travelLayerRef.current) {
      map.removeLayer(travelLayerRef.current);
      travelLayerRef.current = null;
    }
    if (travelRing && travelRing.length >= 3) {
      travelLayerRef.current = L.polygon(travelRing, {
        color: "#8a8a8a", weight: 2, fillColor: "#8a8a8a", fillOpacity: 0.06,
        dashArray: "2,5", interactive: false,
      }).addTo(map);
    }
  }, [travelRing]);

  // ── Cross-view focus: when another view (e.g. Comp Database) requests
  //    that we focus on a specific comp, fly to its pin, ensure its layer
  //    is on, expand any cluster covering it, and open the drawer.
  //    Re-runs whenever properties, layers, or markers change so the focus
  //    eventually fires once everything is in place.
  useEffect(() => {
    if (!focusTarget) return;
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    // ── Deal focus ─────────────────────────────────────────────────────
    // A deal can have multiple addresses (each rendered as its own pin —
    // id="<dealId>" for the primary, id="<dealId>_addr1+" for the rest).
    // We turn on the deal's layer, fit-bounds to encompass every pin
    // belonging to the deal, then open the primary pin's tooltip.
    if (focusTarget.type === 'deal') {
      const dealPins = properties.filter(p => p.isPipeline && p.dealId === focusTarget.id);
      if (dealPins.length === 0) return;                          // not loaded yet
      const withCoords = dealPins.filter(p => p.lat && p.lng);
      if (withCoords.length === 0) {
        showToast("No coordinates for this deal — can't place on map.");
        if (onFocusConsumed) onFocusConsumed();
        return;
      }
      const primary = withCoords[0];
      if (!layers[primary.layerKey]) {
        setLayers(prev => ({ ...prev, [primary.layerKey]: true }));
        return;                                                   // wait for re-render
      }
      // Activate focus mode: append this deal to the focused list if not
      // already there. App.js owns the list (mapFocusDeals); we just call
      // the setter callback. The dedicated zoom effect handles framing.
      if (!focusedDealIds.includes(focusTarget.id)) {
        onChangeFocusedDealIds?.([...focusedDealIds, focusTarget.id].slice(0, MAX_FOCUSED_DEALS));
      }
      const markers = markersRef.current.filter(m =>
        m._ppProp && m._ppProp.isPipeline && m._ppProp.dealId === focusTarget.id
      );
      if (markers.length === 0) return;                           // markers not built yet

      map.once('moveend', () => {
        const primaryMarker = markers.find(m => m._ppProp.id === primary.id) || markers[0];
        const group = clusterGroupsRef.current[primary.layerKey];
        const pinTooltip = () => {
          if (pinnedMarkerRef.current && pinnedMarkerRef.current !== primaryMarker) {
            try { pinnedMarkerRef.current.unbindTooltip(); if (pinnedMarkerRef.current._ppProp) pinnedMarkerRef.current.bindTooltip(buildTipHtml(pinnedMarkerRef.current._ppProp), { sticky: true, className: "pp-tip" }); } catch (e) {}
          }
          primaryMarker.unbindTooltip();
          primaryMarker.bindTooltip(buildTipHtml(primary), { permanent: true, className: "pp-tip", direction: "top", offset: [0, -10] });
          primaryMarker.openTooltip();
          pinnedMarkerRef.current = primaryMarker;
        };
        if (markers.length === 1 && group && typeof group.zoomToShowLayer === 'function') {
          group.zoomToShowLayer(primaryMarker, pinTooltip);
        } else {
          pinTooltip();
        }
      });
      if (onFocusConsumed) onFocusConsumed();
      return;
    }

    if (focusTarget.type !== 'comp') return;

    const targetId = 'comp_' + focusTarget.id;
    const prop = properties.find(p => p.id === targetId);
    if (!prop) return; // not loaded yet
    if (!prop.lat || !prop.lng) {
      showToast("No coordinates for this comp — can't place on map.");
      if (onFocusConsumed) onFocusConsumed();
      return;
    }

    // 1. Turn on the comp's layer if it's off — otherwise the pin isn't rendered.
    if (!layers[prop.layerKey]) {
      setLayers(prev => ({ ...prev, [prop.layerKey]: true }));
      return; // wait for the next render with the layer enabled
    }

    // 2. Find the marker. If it's not in markersRef yet (sync effect hasn't
    //    rebuilt them after the layer toggle), wait for the next pass.
    const marker = markersRef.current.find(m => m._ppProp?.id === targetId);
    if (!marker) return;

    // 3. Fly to the pin first, then once the camera lands, ask the cluster
    //    group to spiderfy/zoom so the individual marker is visible (not
    //    swallowed by a cluster bubble), then open the popup + drawer.
    map.flyTo([prop.lat, prop.lng], 18, { duration: 0.9 });
    map.once('moveend', () => {
      const group = clusterGroupsRef.current[prop.layerKey];
      const pinTooltip = () => {
        if (pinnedMarkerRef.current && pinnedMarkerRef.current !== marker) {
          try { pinnedMarkerRef.current.unbindTooltip(); if (pinnedMarkerRef.current._ppProp) pinnedMarkerRef.current.bindTooltip(buildTipHtml(pinnedMarkerRef.current._ppProp), { sticky: true, className: "pp-tip" }); } catch (e) {}
        }
        marker.unbindTooltip();
        marker.bindTooltip(buildTipHtml(prop), { permanent: true, className: "pp-tip", direction: "top", offset: [0, -10] });
        marker.openTooltip();
        pinnedMarkerRef.current = marker;
      };
      if (group && typeof group.zoomToShowLayer === 'function') {
        group.zoomToShowLayer(marker, pinTooltip);
      } else {
        pinTooltip();
      }
    });
    if (onFocusConsumed) onFocusConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTarget, properties, layers]);

  // ── Basemap switcher ────────────────────────────────────────────────────
  function switchBasemap(key) {
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;
    Object.values(basemapRef.current).forEach(l => { if (l && map.hasLayer(l)) map.removeLayer(l); });
    // Rebuild street tile for current dark/light mode
    if (key === "street") {
      basemapRef.current.street = darkMode
        ? L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", { attribution: "© CartoDB", maxZoom: 19 })
        : L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 });
    }
    if (basemapRef.current[key]) basemapRef.current[key].addTo(map);
    setBasemapState(key);
  }

  // ── Snapshot to PNG (Phase 5.2) ──────────────────────────────────────
  // Captures the .map-area DIV (Leaflet tiles + every overlay including the
  // focused chips + Deal Summary card) via html2canvas, then copies the PNG
  // to clipboard. Falls back to a file download on browsers without the
  // Clipboard.write API (Safari ≤ 16, Firefox pre-127).
  async function handleSnapshot() {
    const node = mapAreaRef.current;
    if (!node) return;
    setSnapshotBusy(true);
    try {
      // useCORS=true is required for the satellite tile layer to render
      // (Esri tiles are served with Access-Control-Allow-Origin: *).
      const canvas = await html2canvas(node, {
        useCORS: true,
        backgroundColor: null,
        logging: false,
        scale: window.devicePixelRatio || 1,
      });
      const blob = await new Promise(res => canvas.toBlob(res, "image/png", 0.95));
      if (!blob) throw new Error("canvas.toBlob returned null");
      // Try clipboard first
      const supportsClipboardItem = typeof window !== "undefined" && window.ClipboardItem
        && navigator.clipboard && navigator.clipboard.write;
      if (supportsClipboardItem) {
        try {
          await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
          showToast("✓ Map snapshot copied — paste into your slide deck", 4000);
          return;
        } catch (clipErr) {
          console.warn("Clipboard write failed; falling back to download:", clipErr);
        }
      }
      // Fallback: trigger a download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      const dealPart = focusedDealIds.length > 0
        ? (focusedDealIds.map(id => focusedDealsData.get(id)?.name || "deal").join("_").replace(/[^a-z0-9_-]+/gi, "-").slice(0, 60))
        : "map";
      a.download = `proxy-snapshot-${dealPart}-${today}.png`;
      a.href = url;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      showToast("✓ Snapshot saved to your downloads", 4000);
    } catch (e) {
      console.error("Snapshot failed:", e);
      showToast(`Snapshot failed: ${e.message || e}`, 5000);
    } finally {
      setSnapshotBusy(false);
    }
  }

  // ── Toast helper ─────────────────────────────────────────────────────────
  function showToast(msg, duration = 3000) {
    setToast(msg);
    setTimeout(() => setToast(null), duration);
  }

  // ── Parcel query ────────────────────────────────────────────────────────
  function identifyCounty(lat, lng) {
    // Florida
    if (lat >= 25.1  && lat <= 25.96 && lng >= -80.9  && lng <= -80.0)  return "miami-dade";
    if (lat >= 25.96 && lat <= 26.32 && lng >= -80.48 && lng <= -80.09) return "broward";
    if (lat >= 26.32 && lat <= 26.97 && lng >= -80.88 && lng <= -80.03) return "palm-beach";
    if (lat >= 27.6  && lat <= 28.17 && lng >= -82.85 && lng <= -82.58) return "pinellas";
    if (lat >= 27.7  && lat <= 28.2  && lng >= -82.8  && lng <= -82.2)  return "hillsborough";
    if (lat >= 28.3  && lat <= 28.8  && lng >= -81.6  && lng <= -81.1)  return "orange";
    // Texas
    if (lat >= 29.4  && lat <= 30.2  && lng >= -95.8  && lng <= -94.8)  return "harris";
    if (lat >= 32.5  && lat <= 33.1  && lng >= -97.1  && lng <= -96.5)  return "dallas";
    if (lat >= 32.5  && lat <= 33.0  && lng >= -97.6  && lng <= -97.0)  return "tarrant";
    // Georgia
    if (lat >= 33.4  && lat <= 33.9  && lng >= -84.8  && lng <= -84.0)  return "fulton";
    return null;
  }

  function parcelQueryUrl(lat, lng, county) {
    const pt = `geometry=${lng},${lat}&geometryType=esriGeometryPoint&spatialRel=esriSpatialRelIntersects&inSR=4326&returnGeometry=true&outSR=4326&f=json&resultRecordCount=1`;
    const buf = `${pt}&distance=15&units=esriSRUnit_Meter`;
    // Tarrant needs envelope query (point queries fail due to State Plane projection)
    const d = 0.0003;
    const env = `geometry=${lng-d},${lat-d},${lng+d},${lat+d}&geometryType=esriGeometryEnvelope&spatialRel=esriSpatialRelIntersects&inSR=4326&returnGeometry=true&outSR=4326&f=json&resultRecordCount=1`;
    const endpoints = {
      "miami-dade":   `https://gis.miamidade.gov/arcgis/rest/services/MD_LandInformation/MapServer/26/query?${buf}&outFields=FOLIO,TRUE_OWNER1,TRUE_SITE_ADDR,TRUE_SITE_CITY,TRUE_SITE_ZIP_CODE,DOR_DESC,LOT_SIZE,BUILDING_HEATED_AREA,YEAR_BUILT,FLOOR_COUNT,UNIT_COUNT,PRIMARY_ZONE`,
      "broward":      `https://services.arcgis.com/JMAJrTsHNLrSsWf5/arcgis/rest/services/PARCEL_POLY_BCPA_TAXROLL/FeatureServer/0/query?${pt}&outFields=*`,
      "palm-beach":   `https://services1.arcgis.com/ZWOoUZbtaYePLlPw/arcgis/rest/services/Parcels_and_Property_Details_WebMercator/FeatureServer/0/query?${pt}&outFields=*`,
      "pinellas":     `https://egis.pinellas.gov/gis/rest/services/AGO/Parcels/MapServer/0/query?${buf}&outFields=*`,
      "hillsborough": `https://gis.hcpafl.org/arcgis/rest/services/Webmaps/HillsboroughFL_WebParcels/MapServer/0/query?${buf}&outFields=*`,
      "orange":       `https://vgispublic.ocpafl.org/server/rest/services/Webmap/PARCEL/MapServer/4/query?${buf}&outFields=*`,
      "harris":       `https://www.gis.hctx.net/arcgis/rest/services/HCAD/Parcels/MapServer/0/query?${buf}&outFields=*`,
      "dallas":       `https://maps.dcad.org/prdwa/rest/services/Property/ParcelQuery/MapServer/4/query?${pt}&outFields=*`,
      "tarrant":      `https://mapit.tarrantcounty.com/arcgis/rest/services/Tax/TCProperty/MapServer/0/query?${env}&outFields=*`,
      "fulton":       `https://gismaps.fultoncountyga.gov/arcgispub2/rest/services/PropertyMapViewer/PropertyMapViewer/MapServer/11/query?${pt}&outFields=*`,
    };
    return endpoints[county] || null;
  }

  async function queryParcel(lat, lng) {
    const county = identifyCounty(lat, lng);
    if (!county) { showToast("No parcel data available for this location."); return; }

    showToast("Querying parcel data…");
    const url = parcelQueryUrl(lat, lng, county);
    if (!url) return;

    try {
      const resp = await fetch(url);
      const json = await resp.json();
      if (!json.features || json.features.length === 0) { showToast("No parcel found at this location."); return; }
      const f = json.features[0];
      const a = f.attributes || {};

      // Normalise attributes per county
      const countyLabel = county.split("-").map(w => w[0].toUpperCase() + w.slice(1)).join("-");
      let info = { county: countyLabel, lat, lng };
      if (county === "miami-dade") {
        info = { ...info, owner: a.TRUE_OWNER1, address: a.TRUE_SITE_ADDR, city: a.TRUE_SITE_CITY, use: a.DOR_DESC, sqft: a.LOT_SIZE, buildingSqft: a.BUILDING_HEATED_AREA, yearBuilt: a.YEAR_BUILT, floors: a.FLOOR_COUNT, units: a.UNIT_COUNT, zoning: a.PRIMARY_ZONE };
        // Secondary query for assessed values via ComparableSales layer
        const folio = a.FOLIO;
        if (folio) {
          try {
            const valResp = await fetch(`https://gis.miamidade.gov/arcgis/rest/services/MD_ComparableSales/MapServer/5/query?where=FOLIO%3D%27${folio}%27&outFields=TOTAL_VAL_PRI,LAND_VAL_PRI,BUILDING_VAL_PRI,ASSESSED_VAL_PRI,PRICE_1,DOS_1&f=json`);
            const valJson = await valResp.json();
            if (valJson.features && valJson.features.length > 0) {
              const v = valJson.features[0].attributes;
              info.totalValue = v.TOTAL_VAL_PRI || v.ASSESSED_VAL_PRI;
              info.landValue = v.LAND_VAL_PRI;
              info.improvement = v.BUILDING_VAL_PRI;
              if (v.PRICE_1) info.lastSale = "$" + Number(v.PRICE_1).toLocaleString();
              if (v.DOS_1) {
                const ds = String(v.DOS_1);
                info.lastSaleDate = ds.length === 8 ? `${ds.slice(4,6)}/${ds.slice(6,8)}/${ds.slice(0,4)}` : ds;
              }
            }
          } catch (e) {}
        }
      } else if (county === "broward") {
        const addr = [a.SITUS_STREET_NUMBER, a.SITUS_STREET_DIRECTION, a.SITUS_STREET_NAME, a.SITUS_STREET_TYPE, a.SITUS_STREET_POST_DIR].filter(Boolean).join(" ");
        let saleDate = null;
        if (a.SALE_DATE_1) { try { saleDate = new Date(a.SALE_DATE_1).toLocaleDateString("en-US"); } catch {} }
        let salePrice = null;
        if (a.STAMP_AMOUNT_1 && a.STAMP_AMOUNT_1 > 0) salePrice = "$" + Math.round(a.STAMP_AMOUNT_1 / 0.007).toLocaleString();
        info = { ...info, owner: a.NAME_LINE_1, address: addr, city: a.SITUS_CITY, use: a.USE_TYPE, totalValue: (a.JUST_LAND_VALUE || 0) + (a.JUST_BUILDING_VALUE || 0), landValue: a.JUST_LAND_VALUE, improvement: a.JUST_BUILDING_VALUE, sqft: a.GIS_SQUARE_FOOT, buildingSqft: a.BLDG_TOT_SQ_FOOTAGE, yearBuilt: a.BLDG_YEAR_BUILT || a.ACTUAL_YEAR_BUILT, lastSaleDate: saleDate, lastSale: salePrice };
      } else if (county === "palm-beach") {
        let saleDate = null;
        if (a.SALE_DATE) { try { saleDate = new Date(a.SALE_DATE).toLocaleDateString("en-US"); } catch {} }
        info = { ...info, owner: a.OWNER_NAME1, address: a.SITE_ADDR_STR || a.PADDR1, city: a.CITYNAME, use: a.PROPERTY_USE, totalValue: a.TOTAL_MARKET || a.TOTAL_VALUE, landValue: a.LAND_MARKET, improvement: a.IMPRV_MRKT, acreage: a.ACRES, yearBuilt: a.YRBLT, lastSaleDate: saleDate, lastSale: a.PRICE ? "$" + Number(a.PRICE).toLocaleString() : null };
      } else if (county === "pinellas") {
        const pfx = "PGIS.PGIS.PAOGENERAL.";
        const ppfx = "PGIS.PGIS.ParcelsPublic.";
        const g = (k) => a[pfx + k] || a[k];
        const p = (k) => a[ppfx + k] || a[k];
        const addr = [g("SITE_NUM"), g("SITE_ST")].filter(Boolean).join(" ");
        let saleDate = null, salePrice = null;
        const sd = g("SALEDATE1"); if (sd) { try { saleDate = new Date(sd).toLocaleDateString("en-US"); } catch {} }
        const sp = g("SALEPRICE1"); if (sp && sp > 0) salePrice = "$" + Number(sp).toLocaleString();
        info = { ...info, owner: g("OWNER1"), address: addr, city: g("SITE_CITY"), totalValue: g("TAXABLE_VALUE"), landValue: g("LAND_VALUE"), improvement: g("IMP_VALUE"), acreage: p("STATEDAREA"), lastSaleDate: saleDate, lastSale: salePrice };
      } else if (county === "hillsborough") {
        info = { ...info, owner: a.Owner1, address: a.FullAddress || a.StreetLabel, city: a.SiteCity, totalValue: a.TopSalePrice ? "$" + Number(a.TopSalePrice).toLocaleString() : null, lastSale: a.TopSalePrice ? "$" + Number(a.TopSalePrice).toLocaleString() : null, lastSaleDate: a.TopSaleDate || null };
      } else if (county === "orange") {
        let saleDate = null;
        if (a.SALE_DATE) { try { saleDate = new Date(a.SALE_DATE).toLocaleDateString("en-US"); } catch {} }
        info = { ...info, owner: a.NAME1, address: a.SITUS, city: a.SITUS_CITY || a.CITY_SITUS, use: a.DOR_CODE, totalValue: a.TOTAL_MKT, landValue: a.LAND_MKT, improvement: a.BLDG_MKT, buildingSqft: a.LIVING_AREA, sqft: a.DEEDED_SQFT, acreage: a.ACREAGE, yearBuilt: a.AYB, floors: a.STYS, lastSaleDate: saleDate, lastSale: a.SALE_ADJ_VALUE ? "$" + Number(a.SALE_ADJ_VALUE).toLocaleString() : null };
      } else if (county === "harris") {
        const addr = [a.site_str_num, a.site_str_name, a.site_str_sfx].filter(Boolean).join(" ");
        let saleDate = null;
        if (a.new_owner_date) { try { saleDate = new Date(a.new_owner_date).toLocaleDateString("en-US"); } catch {} }
        info = { ...info, owner: a.owner_name_1, address: addr, city: a.site_city, totalValue: a.total_appraised_val || a.total_market_val, landValue: a.land_value, improvement: a.impr_value, sqft: a.land_sqft, acreage: a.acreage_1, yearBuilt: a.yr_built, lastSaleDate: saleDate };
      } else if (county === "dallas") {
        let saleDate = null;
        if (a.LASTUPDATE) { try { saleDate = new Date(a.LASTUPDATE).toLocaleDateString("en-US"); } catch {} }
        info = { ...info, owner: a.OWNERNME1, address: a.SITEADDRESS, use: a.USEDSCRP, totalValue: a.CNTASSDVAL, landValue: a.LNDVALUE, improvement: a.IMPVALUE, buildingSqft: a.BLDGAREA, acreage: a.STATEDAREA, yearBuilt: a.RESYRBLT, floors: a.FLOORCOUNT, lastSaleDate: saleDate };
      } else if (county === "tarrant") {
        let saleDate = null;
        if (a.DEED_DATE) { try { saleDate = new Date(a.DEED_DATE).toLocaleDateString("en-US"); } catch {} }
        info = { ...info, owner: a.OWNER_NAME, address: a.SITUS_ADDR, city: a.CITY, totalValue: a.TOTAL_VALU, landValue: a.LAND_VALUE, improvement: a.IMPR_VALUE, sqft: a.LAND_SQFT, acreage: a.LAND_ACRES, yearBuilt: a.YEAR_BUILT, buildingSqft: a.LIVING_ARE, lastSaleDate: saleDate, lastSale: a.TOTAL_VALU ? "$" + Number(a.TOTAL_VALU).toLocaleString() : null };
      } else if (county === "fulton") {
        info = { ...info, owner: a.Owner, address: a.Address, totalValue: a.TotAppr, landValue: a.LandAppr, improvement: a.ImprAppr, acreage: a.LandAcres, use: a.LUCode, units: a.LivUnits };
      }

      // Draw parcel polygon
      const L = window.L;
      const map = mapInstanceRef.current;
      if (L && map) {
        if (parcelLayerRef.current) map.removeLayer(parcelLayerRef.current);
        if (f.geometry && f.geometry.rings) {
          const geojson = { type: "Polygon", coordinates: f.geometry.rings };
          parcelLayerRef.current = L.geoJSON(geojson, { style: { color: "#e93529", weight: 2, fillOpacity: 0.08, fillColor: "#e93529", dashArray: "5,5" } }).addTo(map);
        }
      }

      setParcelInfo(info);
      showToast(`Parcel found: ${info.owner || "unknown owner"}`);
    } catch (e) {
      showToast("Parcel query failed: " + e.message);
    }
  }

  function toggleParcelMode() {
    const next = !parcelModeRef.current;
    parcelModeRef.current = next;
    setParcelMode(next);
    const map = mapInstanceRef.current;
    if (map) map.getContainer().style.cursor = next ? "crosshair" : "";
    showToast(next ? "Click on the map to query parcel data." : "Parcel mode off.");
  }

  // ── Street View toggle ──────────────────────────────────────────────────
  function toggleStreetView() {
    const next = !streetViewModeRef.current;
    streetViewModeRef.current = next;
    setStreetViewMode(next);
    if (!next) {
      setStreetViewCoords(null);
      if (streetViewPanoRef.current) { streetViewPanoRef.current = null; }
    }
    const map = mapInstanceRef.current;
    if (map) map.getContainer().style.cursor = next ? "crosshair" : (parcelModeRef.current ? "crosshair" : "");
    showToast(next ? "Click on the map to open Street View." : "Street View off.");
  }

  function openStreetView(lat, lng) {
    setStreetViewCoords({ lat, lng });
    // Initialize Google Street View panorama after React renders the container
    setTimeout(() => {
      if (!streetViewRef.current || !window.google?.maps) return;
      const sv = new window.google.maps.StreetViewService();
      sv.getPanorama({ location: { lat, lng }, radius: 100 }, (data, status) => {
        if (status === "OK") {
          streetViewPanoRef.current = new window.google.maps.StreetViewPanorama(streetViewRef.current, {
            position: data.location.latLng,
            pov: { heading: 0, pitch: 0 },
            zoom: 1,
            addressControl: false,
            fullscreenControl: true,
            motionTracking: false,
          });
        } else {
          showToast("No Street View available at this location.");
          setStreetViewCoords(null);
        }
      });
    }, 100);
  }

  // ── Street View map click handler ──────────────────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    function handleSVClick(e) {
      if (!streetViewModeRef.current) return;
      openStreetView(e.latlng.lat, e.latlng.lng);
    }
    map.on("click", handleSVClick);
    return () => { map.off("click", handleSVClick); };
  });

  // ── Search autocomplete (debounced external lookup) ──────────────────────
  function onSearchChange(val) {
    setSearch(val);
    // Clear external results if short
    if (val.trim().length < 4) { setSearchExtResults([]); return; }
    // Debounce external geocode suggestions
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const resp = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(val.trim())}&limit=5&lang=en&layer=house&layer=street&bbox=-100,24,-78,34`);
        if (resp.ok) {
          const data = await resp.json();
          const results = (data.features || []).map(f => {
            const p = f.properties || {};
            const parts = [p.housenumber, p.street, p.city, p.state, p.postcode].filter(Boolean);
            return { label: parts.join(", ") || p.name || "", lat: f.geometry?.coordinates?.[1], lng: f.geometry?.coordinates?.[0] };
          }).filter(r => r.label && r.lat);
          setSearchExtResults(results);
        }
      } catch (e) {}
    }, 350);
  }

  function getLocalSuggestions() {
    if (search.trim().length < 2) return [];
    const q = search.toLowerCase();
    return properties.filter(p => {
      const hay = [p.address, p.market, p.owner_name, p._dealName].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    }).slice(0, 8);
  }

  function selectSuggestion(item) {
    if (item._isExternal) {
      // External address — fly and parcel query
      setSearch(item.label);
      setSearchFocused(false);
      setSearchExtResults([]);
      const map = mapInstanceRef.current;
      if (map) map.flyTo([item.lat, item.lng], 17, { duration: 1.5 });
      // Drop temp marker + parcel query
      const L = window.L;
      if (L && map) {
        const icon = L.divIcon({
          html: `<div style="background:#e93529;color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:14px;border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.5)">⊕</div>`,
          className: "", iconSize: [28, 28], iconAnchor: [14, 14],
        });
        const marker = L.marker([item.lat, item.lng], { icon }).addTo(map);
        marker.bindPopup(`<div style="font-family:'Montserrat',sans-serif;font-size:11px"><strong>${item.label}</strong><br><span style="color:#888">Parcel query sent</span></div>`).openPopup();
        setTimeout(() => { if (map.hasLayer(marker)) map.removeLayer(marker); }, 30000);
      }
      queryParcel(item.lat, item.lng);
    } else {
      // Database property — fly to it
      setSearch(item.address || "");
      setSearchFocused(false);
      if (item.lat && item.lng) {
        const map = mapInstanceRef.current;
        if (map) map.flyTo([item.lat, item.lng], 16, { duration: 1.5 });
      }
    }
  }

  // ── Search: filter database OR geocode + parcel lookup ───────────────────
  async function handleSearchAddress() {
    if (!search.trim()) return;
    const q = search.toLowerCase();

    // Check if any database/pipeline properties match
    const matches = properties.filter(p => {
      const hay = [p.address, p.market, p.stage, p.owner_name, p._dealName].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });

    if (matches.length > 0) {
      // Database match found — fly to the first one with coordinates
      const withCoords = matches.find(p => p.lat && p.lng);
      if (withCoords) {
        const map = mapInstanceRef.current;
        if (map) map.flyTo([withCoords.lat, withCoords.lng], 15, { duration: 1.5 });
        showToast(`Found ${matches.length} match${matches.length > 1 ? "es" : ""} in sourcing.`);
      }
      return; // search filter already handles display via the markers effect
    }

    // No sourcing match — try geocoding the address and querying parcel data
    showToast("No sourcing match. Geocoding address...");
    const coords = await geocodeForView(search.trim());
    if (!coords) {
      showToast("Could not geocode that address.");
      return;
    }

    // Fly to the location
    const map = mapInstanceRef.current;
    if (map) map.flyTo([coords.lat, coords.lng], 17, { duration: 1.5 });

    // Drop a temporary marker
    const L = window.L;
    if (L && map) {
      const icon = L.divIcon({
        html: `<div style="background:#e93529;color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:14px;border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.5)">⊕</div>`,
        className: "", iconSize: [28, 28], iconAnchor: [14, 14],
      });
      const marker = L.marker([coords.lat, coords.lng], { icon }).addTo(map);
      marker.bindPopup(`<div style="font-family:'Montserrat',sans-serif;font-size:11px"><strong>${search.trim()}</strong><br><span style="color:#888">Not in sourcing — parcel query sent</span></div>`).openPopup();
      // Remove marker after 30 seconds
      setTimeout(() => { if (map.hasLayer(marker)) map.removeLayer(marker); }, 30000);
    }

    // Auto-query parcel data
    await queryParcel(coords.lat, coords.lng);
  }

  // ── Parcel map click handler ────────────────────────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    function handleClick(e) {
      if (!parcelModeRef.current) return;
      queryParcel(e.latlng.lat, e.latlng.lng);
    }
    map.on("click", handleClick);
    return () => { map.off("click", handleClick); };
  });

  // ── Radius draw map click handler ─────────────────────────────────────
  // When the user toggles "Draw" in the radius section, the next click sets
  // the radius center. Mode auto-disables after one click so a misclick
  // doesn't keep moving the center on subsequent map interactions.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    function handleClick(e) {
      if (!radiusDrawModeRef.current) return;
      const { lat, lng } = e.latlng;
      setRadiusCenter({ lat, lng, label: `${lat.toFixed(4)}, ${lng.toFixed(4)}` });
      setRadiusInput("");
      radiusDrawModeRef.current = false;
      setRadiusDrawMode(false);
      map.getContainer().style.cursor = (parcelModeRef.current || streetViewModeRef.current) ? "crosshair" : "";
      showToast(`Radius centered at ${lat.toFixed(4)}, ${lng.toFixed(4)}.`);
    }
    map.on("click", handleClick);
    return () => { map.off("click", handleClick); };
  });

  // ── Travel origin click handler ───────────────────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    function handleClick(e) {
      if (!travelDrawModeRef.current) return;
      const { lat, lng } = e.latlng;
      setTravelOrigin({ lat, lng });
      travelDrawModeRef.current = false;
      setTravelDrawMode(false);
      map.getContainer().style.cursor = (parcelModeRef.current || streetViewModeRef.current) ? "crosshair" : "";
      showToast("Origin set. Choose the minutes, then Apply.");
    }
    map.on("click", handleClick);
    return () => { map.off("click", handleClick); };
  });

  // ── Boundary draw map handlers ────────────────────────────────────────
  // Click drops a vertex. Clicking the first vertex again (or double-click
  // anywhere) closes the shape. Double-click zoom is suspended while drawing
  // so closing the polygon doesn't also zoom the map.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    function handleClick(e) {
      if (!boundaryDrawModeRef.current) return;
      const { lat, lng } = e.latlng;
      const draft = boundaryDraftRef.current;
      // Closing click: within 12px of the first vertex and we have a shape.
      if (draft.length >= 3) {
        const first = map.latLngToContainerPoint(draft[0]);
        const here = map.latLngToContainerPoint([lat, lng]);
        if (first.distanceTo(here) <= 12) { commitBoundary(); return; }
      }
      const next = [...draft, [lat, lng]];
      boundaryDraftRef.current = next;
      setBoundaryDraft(next);
    }

    function handleDblClick() {
      if (!boundaryDrawModeRef.current) return;
      commitBoundary();
    }

    map.on("click", handleClick);
    map.on("dblclick", handleDblClick);
    return () => { map.off("click", handleClick); map.off("dblclick", handleDblClick); };
  });

  // Keyboard shortcuts while drawing: Enter closes, Backspace undoes the last
  // vertex, Escape abandons the draft.
  useEffect(() => {
    if (!boundaryDrawMode) return;
    function onKey(e) {
      if (e.key === "Enter") { e.preventDefault(); commitBoundary(); }
      else if (e.key === "Escape") { e.preventDefault(); cancelBoundaryDraw(); }
      else if (e.key === "Backspace") {
        e.preventDefault();
        const next = boundaryDraftRef.current.slice(0, -1);
        boundaryDraftRef.current = next;
        setBoundaryDraft(next);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryDrawMode]);

  // Fit the viewport to a boundary as soon as it is committed or loaded.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !boundary) return;
    const b = polygonBounds(boundary);
    if (b) map.fitBounds(b, { padding: [50, 50] });
  }, [boundary]);

  // Load the team's saved boundaries once. If migration 018 hasn't been run
  // the store reports "not installed" and only the save button is disabled —
  // drawing, filtering and export all still work.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { installed, boundaries } = await loadMapBoundaries();
        if (cancelled) return;
        setBoundaryStoreReady(installed);
        setSavedBoundaries(boundaries);
      } catch (err) {
        if (!cancelled) setBoundaryStoreReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);



  // The "Refresh" button that used to live here geocoded every deal and
  // Sourcing property with no coordinates and wrote the answer back to
  // Supabase - restricted to the US and nothing narrower, so a bare street
  // address could land in any state. Removed 2026-09-29 at Daniel's request.
  // Pins are set by the Map/ loaders, which enforce the state and check the
  // house number survives; a stored pin is permanent.

  // ── Add parcel to Sourcing (LOI Sender) ──────────────────────────────────
  // Creates a property row from the Parcel Info card data. upsertProperty
  // handles dedup by address, so clicking twice is harmless. The new property
  // will land in Ready to Send or Missing Contact depending on whether the
  // owner name matches an existing contact with an email.
  async function handleAddParcelToSourcing() {
    if (!parcelInfo || addingSourcing) return;
    setAddingSourcing(true);
    try {
      const prop = {
        address: parcelInfo.address || '',
        city: parcelInfo.city || '',
        state: parcelInfo.state || 'FL',
        market: parcelInfo.county || '',
        owner_name: parcelInfo.owner || '',
        rba: parcelInfo.buildingSqft ? Number(parcelInfo.buildingSqft) : null,
        year_built: parcelInfo.yearBuilt || null,
        for_sale_price: parcelInfo.lastSale ? Number(String(parcelInfo.lastSale).replace(/[$,]/g, '')) || null : null,
        for_sale_status: null,
        percent_leased: null,
        ceiling_ht: null,
        loading_docks: null,
      };
      // Geocode coords from the parcel click point if available
      if (parcelInfo.lat && parcelInfo.lng) {
        prop.latitude = parcelInfo.lat;
        prop.longitude = parcelInfo.lng;
      }
      await upsertProperty(prop);
      showToast(`Added to Sourcing: ${prop.address}`, 4000);
    } catch (err) {
      console.error('Add to Sourcing error:', err);
      showToast(`Failed to add: ${err.message}`, 5000);
    } finally {
      setAddingSourcing(false);
    }
  }

  // ── Radius search handlers ──────────────────────────────────────────────
  async function applyRadiusSearch() {
    const addr = radiusInput.trim();
    if (!addr) return;
    setRadiusGeocoding(true);
    try {
      const coords = await geocodeForView(addr);
      if (coords?.lat && coords?.lng) {
        setRadiusCenter({ lat: coords.lat, lng: coords.lng, label: addr });
        const map = mapInstanceRef.current;
        if (map) map.flyTo([coords.lat, coords.lng], 12, { duration: 0.6 });
      } else {
        showToast("Address not found.");
      }
    } catch (e) {
      showToast("Geocoding failed: " + (e.message || "unknown error"));
    } finally {
      setRadiusGeocoding(false);
    }
  }

  function clearRadiusSearch() {
    setRadiusCenter(null);
    setRadiusInput("");
    if (radiusDrawModeRef.current) {
      radiusDrawModeRef.current = false;
      setRadiusDrawMode(false);
      const map = mapInstanceRef.current;
      if (map) map.getContainer().style.cursor = (parcelModeRef.current || streetViewModeRef.current) ? "crosshair" : "";
    }
  }

  function toggleRadiusDrawMode() {
    const next = !radiusDrawModeRef.current;
    radiusDrawModeRef.current = next;
    setRadiusDrawMode(next);
    const map = mapInstanceRef.current;
    if (map) map.getContainer().style.cursor = next ? "crosshair" : (parcelModeRef.current || streetViewModeRef.current) ? "crosshair" : "";
    showToast(next ? "Click anywhere on the map to set the radius center." : "Radius draw off.");
  }

  // Location options come from every loaded pin, not the filtered set, so
  // clearing one selection does not make the others vanish from the list.
  const locationOpts = useMemo(() => ({
    market:    locationOptions(properties, "market"),
    submarket: locationOptions(properties, "submarket"),
    city:      locationOptions(properties, "city"),
  }), [properties]);

  const activeFilterCount = activeAttrCount(attrFilters) + activeLocationCount(locSel);

  function setLocationField(field, nextSet) {
    setLocSel(prev => ({ ...prev, [field]: nextSet }));
  }
  function clearAllLocation() {
    setLocSel({ market: new Set(), submarket: new Set(), city: new Set() });
  }

  // ── Travel handlers ─────────────────────────────────────────────────────
  function toggleTravelDrawMode() {
    const next = !travelDrawModeRef.current;
    const map = mapInstanceRef.current;
    travelDrawModeRef.current = next;
    setTravelDrawMode(next);
    if (next) {
      radiusDrawModeRef.current = false; setRadiusDrawMode(false);
      if (boundaryDrawModeRef.current) cancelBoundaryDraw();
      if (map) map.getContainer().style.cursor = "crosshair";
      showToast("Click the map to set the drive-time origin.");
    } else if (map) {
      map.getContainer().style.cursor = (parcelModeRef.current || streetViewModeRef.current) ? "crosshair" : "";
    }
  }

  async function runTravel() {
    if (!travelOrigin) { showToast("Set an origin on the map first."); return; }
    setTravelBusy(true);
    try {
      const { ring, elementsUsed, unreachableRays } =
        await buildIsochrone(travelOrigin, travelMinutes);
      setTravelRing(ring);
      setTravelMeta({ elementsUsed, unreachableRays, minutes: travelMinutes });
      const map = mapInstanceRef.current;
      const b = polygonBounds(ring);
      if (map && b) map.fitBounds(b, { padding: [50, 50] });
      showToast(
        `${travelMinutes}-minute drive time applied (approximate)` +
        (unreachableRays > 0 ? ` — ${unreachableRays} direction${unreachableRays === 1 ? "" : "s"} had no route.` : "."),
        5000
      );
    } catch (err) {
      showToast(`Drive time failed: ${err.message}`, 6000);
    } finally {
      setTravelBusy(false);
    }
  }

  function clearTravel() {
    setTravelRing(null);
    setTravelOrigin(null);
    setTravelMeta(null);
    if (travelDrawModeRef.current) toggleTravelDrawMode();
    showToast("Drive-time filter cleared.");
  }

  // ── Property page navigation ────────────────────────────────────────────
  // Distinct addresses across everything currently drawn, in map order —
  // this is what prev/next steps through.
  const addressList = useMemo(() => {
    const seen = new Map();
    visiblePins.forEach(p => {
      const k = addressKey(p.address);
      if (!k) return;
      if (!seen.has(k)) seen.set(k, p.address);
    });
    return [...seen.entries()].map(([key, address]) => ({ key, address }));
  }, [visiblePins]);

  const propertyPageIndex = propertyAddrKey == null
    ? -1
    : addressList.findIndex(a => a.key === propertyAddrKey);
  const propertyAddress = propertyPageIndex >= 0 ? addressList[propertyPageIndex].address : null;
  // The pins inside the current viewport. The cards, the list and every count
  // read from this, so all three agree with what is actually on screen.
  //
  // A pin with no coordinate is KEPT rather than dropped. It is in the result
  // set; it simply cannot be drawn. Quietly losing those from the list would
  // hide exactly the records that need an address typed into them.
  const pinsInView = useMemo(() => {
    if (!mapBounds) return visiblePins;
    return visiblePins.filter((p) => {
      if (p.lat == null || p.lng == null) return true;
      return p.lat >= mapBounds.s && p.lat <= mapBounds.n
          && p.lng >= mapBounds.w && p.lng <= mapBounds.e;
    });
  }, [visiblePins, mapBounds]);

  const propertyRecords = useMemo(
    () => (propertyAddrKey ? visiblePins.filter(p => addressKey(p.address) === propertyAddrKey) : []),
    [visiblePins, propertyAddrKey]
  );

  function openPropertyPage(prop) {
    const k = addressKey(prop?.address);
    if (k) setPropertyAddrKey(k);
  }

  function stepPropertyPage(delta) {
    const next = propertyPageIndex + delta;
    if (next < 0 || next >= addressList.length) return;
    setPropertyAddrKey(addressList[next].key);
  }

  // ── Boundary handlers ───────────────────────────────────────────────────
  function toggleBoundaryDrawMode() {
    const next = !boundaryDrawModeRef.current;
    const map = mapInstanceRef.current;
    boundaryDrawModeRef.current = next;
    setBoundaryDrawMode(next);
    boundaryDraftRef.current = [];
    setBoundaryDraft([]);
    if (next) {
      // Radius draw and boundary draw both claim map clicks — only one at a time.
      radiusDrawModeRef.current = false;
      setRadiusDrawMode(false);
      if (map) { map.doubleClickZoom.disable(); map.getContainer().style.cursor = "crosshair"; }
      showToast("Click to place boundary corners. Click the first point or double-click to close. Esc cancels.", 6000);
    } else {
      if (map) {
        map.doubleClickZoom.enable();
        map.getContainer().style.cursor = (parcelModeRef.current || streetViewModeRef.current) ? "crosshair" : "";
      }
    }
  }

  function commitBoundary() {
    const draft = boundaryDraftRef.current;
    if (draft.length < 3) { showToast("A boundary needs at least 3 corners."); return; }
    const map = mapInstanceRef.current;
    setBoundary(draft);
    setBoundaryLabel("");
    setBoundaryNameInput("");
    boundaryDrawModeRef.current = false;
    setBoundaryDrawMode(false);
    boundaryDraftRef.current = [];
    setBoundaryDraft([]);
    if (map) {
      map.doubleClickZoom.enable();
      map.getContainer().style.cursor = (parcelModeRef.current || streetViewModeRef.current) ? "crosshair" : "";
    }
    showToast(`Boundary applied — ${polygonAreaSqMi(draft).toFixed(1)} sq mi. Only pins inside are shown.`, 4000);
  }

  function cancelBoundaryDraw() {
    const map = mapInstanceRef.current;
    boundaryDrawModeRef.current = false;
    setBoundaryDrawMode(false);
    boundaryDraftRef.current = [];
    setBoundaryDraft([]);
    if (map) {
      map.doubleClickZoom.enable();
      map.getContainer().style.cursor = (parcelModeRef.current || streetViewModeRef.current) ? "crosshair" : "";
    }
  }

  function clearBoundary() {
    setBoundary(null);
    setBoundaryLabel("");
    setBoundaryNameInput("");
    if (boundaryDrawModeRef.current) cancelBoundaryDraw();
    showToast("Boundary cleared.");
  }

  async function saveCurrentBoundary() {
    const name = boundaryNameInput.trim();
    if (!boundary) { showToast("Draw a boundary first."); return; }
    if (!name) { showToast("Give the boundary a name first."); return; }
    setBoundarySaving(true);
    try {
      const res = await saveMapBoundary(name, boundary, userEmail || null);
      if (res.installed === false) {
        setBoundaryStoreReady(false);
        showToast("Saving needs migration 018_map_boundaries.sql run in Supabase.", 6000);
        return;
      }
      const { boundaries } = await loadMapBoundaries();
      setSavedBoundaries(boundaries);
      setBoundaryLabel(name);
      setBoundaryNameInput("");
      showToast(res.replaced ? `Updated submarket "${name}".` : `Saved submarket "${name}".`);
    } catch (err) {
      showToast(`Could not save boundary: ${err.message}`, 5000);
    } finally {
      setBoundarySaving(false);
    }
  }

  function applySavedBoundary(b) {
    if (!b || !Array.isArray(b.coords) || b.coords.length < 3) {
      showToast("That saved boundary looks malformed.");
      return;
    }
    if (boundaryDrawModeRef.current) cancelBoundaryDraw();
    setBoundary(b.coords);
    setBoundaryLabel(b.name);
    showToast(`Boundary "${b.name}" applied — ${polygonAreaSqMi(b.coords).toFixed(1)} sq mi.`);
  }

  async function removeSavedBoundary(b) {
    try {
      await deleteMapBoundary(b.id);
      setSavedBoundaries(prev => prev.filter(x => x.id !== b.id));
      if (boundaryLabel === b.name) setBoundaryLabel("");
      showToast(`Deleted "${b.name}".`);
    } catch (err) {
      showToast(`Could not delete: ${err.message}`, 5000);
    }
  }

  // Row click in the pin table → close the overlay and fly to that pin.
  function focusPinFromTable(prop) {
    const map = mapInstanceRef.current;
    if (!map || !prop?.lat || !prop?.lng) return;
    map.flyTo([prop.lat, prop.lng], 17, { duration: 0.7 });
  }

  // ── Computed ─────────────────────────────────────────────────────────────
  const layerCounts = {};
  properties.forEach(p => { layerCounts[p.layerKey] = (layerCounts[p.layerKey] || 0) + 1; });
  const pipelineVisible = properties.filter(p => p.isPipeline && p.lat && layers[p.layerKey]).length;
  const dbVisible = properties.filter(p => !p.isPipeline && p.lat && layers[p.layerKey]).length;

  // Deal-focus geo — centroid of the focused deal's address pins, used to
  // constrain which comps render to the metro around that deal. 50 mi
  // covers the major metro markets (DFW, Houston, Miami, Atlanta, etc.)
  // without leaking cross-state comps into the view.
  // Load per-deal data (assigned comps + rent roll + centroid) whenever the
  // focused deal list changes. Keeps focusedDealsData in sync — adds new
  // deals, drops removed ones, leaves the existing entries untouched (so we
  // don't re-fetch on every change).
  useEffect(() => {
    if (focusedDealIds.length === 0) {
      setFocusedDealsData(new Map());
      setSelectedCompIds(new Set());
      setRentRollPanelAddress(null);
      setSelectionMode(false);
      selectionModeRef.current = false;
      setDrawerDealId(null);
      setBulkApplyDealId(null);
      return;
    }
    let cancelled = false;
    // Prefer the deal-table record over pin-derived name — the pin lookup can
    // be empty if the deal's address hasn't been geocoded yet (pins are
    // filtered on lat/lng), which is why the fallback "deal" string was
    // leaking into the UI. Re-resolved on every effect run so a name that
    // started empty fills in once availableDeals or properties arrives.
    const resolveName = (id) => {
      const dealRow = (availableDeals || []).find(d => d.id === id);
      const pin = properties.find(p => p.isPipeline && p.dealId === id);
      return (
        dealRow?._dealName ||
        dealRow?.sourceNotes?.match(/^Name:\s*(.+)$/m)?.[1] ||
        dealRow?.addresses?.[0] ||
        pin?._dealName ||
        pin?.address?.split(",")[0] ||
        "Unnamed deal"
      );
    };
    // Resolve a deal's centroid from either the loaded pipeline pins, or if
    // properties haven't loaded yet, the deal row's own latitude/longitude
    // from availableDeals. Falls back to null only if neither source has coords.
    const resolveCenter = (id) => {
      const pins = properties.filter(p => p.isPipeline && p.dealId === id && p.lat && p.lng);
      if (pins.length > 0) {
        return { lat: pins.reduce((s, p) => s + p.lat, 0) / pins.length,
                 lng: pins.reduce((s, p) => s + p.lng, 0) / pins.length };
      }
      const dealRow = (availableDeals || []).find(d => d.id === id);
      if (dealRow?.latitude && dealRow?.longitude) {
        return { lat: Number(dealRow.latitude), lng: Number(dealRow.longitude) };
      }
      return null;
    };

    (async () => {
      // Figure out which deals are new (not yet in the map) and which to drop
      const next = new Map();
      for (const id of focusedDealIds) {
        if (focusedDealsData.has(id)) {
          // Keep cached comps/rentRoll, but ALWAYS re-resolve name AND center.
          // The initial run may have happened before properties / availableDeals
          // loaded, leaving center null forever (which broke the fit-bounds
          // auto-zoom). Re-resolving fixes that as soon as either source arrives.
          const existing = focusedDealsData.get(id);
          next.set(id, {
            ...existing,
            name: resolveName(id),
            center: existing.center || resolveCenter(id),
          });
          continue;
        }
        // New focus — fetch comps + rent roll
        const [comps, rr] = await Promise.all([
          loadAssignedComps(id),
          loadRentRoll(id),
        ]);
        if (cancelled) return;
        next.set(id, { comps: comps || [], rentRoll: rr || [], center: resolveCenter(id), name: resolveName(id) });
      }
      if (cancelled) return;
      setFocusedDealsData(next);
      // Default the drawer + bulk-apply target to the primary focused deal.
      if (!drawerDealId || !focusedDealIds.includes(drawerDealId)) {
        setDrawerDealId(focusedDealIds[0]);
      }
      if (!bulkApplyDealId || !focusedDealIds.includes(bulkApplyDealId)) {
        setBulkApplyDealId(focusedDealIds[0]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedDealIds, properties, availableDeals]);

  const DEAL_FOCUS_COMP_RADIUS_MI = 50;

  // Deal-focus auto-zoom (multi-deal): fits the union of every focused
  // deal's pins + nearby comp set. Same metro-tight zoom for single-deal,
  // naturally falls back to a wider view for cross-metro comparisons
  // (Florida + Texas etc.). Per D6, no distance restriction.
  //
  // Re-runs when `properties` changes so that if the effect fires before
  // properties has finished loading, we still get a proper zoom-in once
  // the pipeline pins arrive.
  useEffect(() => {
    if (focusedDealIds.length === 0) return;
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;
    const centers = focusedDealIds.map(id => focusedDealsData.get(id)?.center).filter(Boolean);
    if (centers.length === 0) return;
    const inFrame = properties.filter(p => {
      if (!p.lat || !p.lng) return false;
      if (p.isPipeline && focusedDealIds.includes(p.dealId)) return true;
      if (p.isComp && layers[p.layerKey]) {
        for (const c of centers) {
          if (distMiles(c.lat, c.lng, p.lat, p.lng) <= DEAL_FOCUS_COMP_RADIUS_MI) return true;
        }
      }
      return false;
    });
    // Fallback: no properties matched (e.g. properties still loading) —
    // zoom to the deal's own center coordinates directly so the map isn't
    // stuck at the default view.
    if (inFrame.length === 0) {
      // Only centers to work with. A single-point latLngBounds has zero area,
      // and flyToBounds on that is a no-op, so use flyTo instead — jumps to
      // the deal coords at a metro-tight zoom.
      if (centers.length === 1) {
        map.flyTo([centers[0].lat, centers[0].lng], 15, { duration: 0.8 });
      } else {
        const b = L.latLngBounds(centers.map(c => [c.lat, c.lng]));
        if (b.isValid()) map.flyToBounds(b, { padding: [60, 60], maxZoom: 13, duration: 0.8 });
      }
      return;
    }
    // Same single-point defense: if inFrame has exactly one location, use
    // flyTo instead of flyToBounds so the zoom actually happens.
    if (inFrame.length === 1) {
      map.flyTo([inFrame[0].lat, inFrame[0].lng], 15, { duration: 0.8 });
      return;
    }
    const bounds = L.latLngBounds(inFrame.map(p => [p.lat, p.lng]));
    if (bounds.isValid()) {
      map.flyToBounds(bounds, { padding: [60, 60], maxZoom: focusedDealIds.length === 1 ? 15 : 13, duration: 0.8 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedDealIds, focusedDealsData, properties]);




  // ── Styles ──────────────────────────────────────────────────────────────
  const S = darkMode ? {
    font: "'Montserrat', sans-serif",
    dark: "#1c1c1c", sidebar: "#1c1c1c", card: "#242424", card2: "#2c2c2c",
    border: "#2c2c2c", text: "#ebebeb", muted: "#8a8a8a",
    red: "#e93529", redH: "#f04f44",
    overlay: "rgba(0,0,0,0.75)", overlayCard: "rgba(20,20,20,0.95)",
    chipBg: "rgba(0,0,0,0.72)", badgeBg: "rgba(0,0,0,0.65)",
  } : {
    font: "'Montserrat', sans-serif",
    dark: "#f4f4f4", sidebar: "#ffffff", card: "#f0f0f0", card2: "#e4e4e4",
    border: "#d8d8d8", text: "#333333", muted: "#7a7a7a",
    red: "#e93529", redH: "#f04f44",
    overlay: "rgba(255,255,255,0.8)", overlayCard: "rgba(255,255,255,0.96)",
    chipBg: "rgba(255,255,255,0.85)", badgeBg: "rgba(255,255,255,0.8)",
  };

  // ── Tooltip builder (reusable for pin/unpin) ────────────────────────────
  function buildTipHtml(prop, stackCount = 1) {
    const color = MAP_LAYER_COLORS[prop.layerKey] || "#6a6a6a";
    const isDb = !prop.isPipeline;
    const bucketLabel = isDb ? (MAP_LAYER_LABELS[prop.layerKey] || prop.bucket) : (MAP_LAYER_LABELS[prop.layerKey] || prop.layerKey);
    return `<div style="font-family:'Montserrat',sans-serif;line-height:1.4"><strong style="font-size:11px">${prop._dealName || prop.address}</strong><br><span style="font-size:10px;color:${color}">${bucketLabel}</span>${prop.sf ? ` · <span style="font-size:10px">${Number(prop.sf).toLocaleString()} SF</span>` : ""}${prop.owner_name ? `<br><span style="font-size:9px;color:#aaa">${prop.owner_name}</span>` : prop.market ? `<br><span style="font-size:9px;color:#aaa">${prop.market}</span>` : ""}${stackCount > 1 ? `<br><span style="font-size:9px;color:${MAP_LAYER_COLORS[prop.layerKey] || "#aaa"};font-weight:800">${stackCount} records at this address</span>` : ""}</div>`;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: presentMode ? "100vh" : "calc(100vh - 49px)", fontFamily: S.font, background: S.dark, color: S.text, overflow: "hidden" }}>

      {/* ═══ TOP BAR (hidden in Present mode) ════════════════════════════ */}
      {!presentMode && (
      <div style={{ height: 48, minHeight: 48, background: S.sidebar, borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", gap: 10, padding: "0 14px", zIndex: 1000, flexShrink: 0 }}>
        {/* Address / name search — moved out of the sidebar */}
        <div style={{ width: 300, flexShrink: 0 }}>
<div style={{ position: "relative" }}>
                <div style={{ display: "flex", gap: 4 }}>
                  <input value={search} onChange={e => onSearchChange(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onKeyDown={e => { if (e.key === "Enter") { setSearchFocused(false); handleSearchAddress(); } if (e.key === "Escape") setSearchFocused(false); }}
                    placeholder="Name, address, or any US address…"
                    style={{ flex: 1, background: S.card, border: `1px solid ${S.border}`, borderRadius: 7, padding: "7px 9px", color: S.text, fontSize: 11, fontFamily: S.font, outline: "none", boxSizing: "border-box" }} />
                  <button onClick={() => { setSearchFocused(false); handleSearchAddress(); }}
                    style={{ background: S.red, border: "none", borderRadius: 7, color: "#fff", padding: "0 10px", fontSize: 10, fontWeight: 800, fontFamily: S.font, cursor: "pointer", flexShrink: 0 }}>Go</button>
                </div>
                {/* Autocomplete dropdown */}
                {searchFocused && search.trim().length >= 2 && (() => {
                  const local = getLocalSuggestions();
                  const ext = searchExtResults.map(r => ({ ...r, _isExternal: true }));
                  const hasResults = local.length > 0 || ext.length > 0;
                  if (!hasResults) return null;
                  return (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1000, background: S.sidebar, border: `1px solid ${S.border}`, borderRadius: 8, marginTop: 4, maxHeight: 280, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}
                      onMouseDown={e => e.preventDefault()}>
                      {local.length > 0 && (
                        <>
                          <div style={{ padding: "6px 10px 3px", fontSize: 9, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>Sourcing</div>
                          {local.map((p, i) => (
                            <div key={p.id || i} onClick={() => selectSuggestion(p)}
                              style={{ padding: "6px 10px", cursor: "pointer", fontSize: 11, color: S.text, borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", gap: 8 }}
                              onMouseEnter={e => e.currentTarget.style.background = S.card}
                              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {p._dealName ? <><strong>{p._dealName}</strong> <span style={{ color: S.muted, fontSize: 10 }}>· {p.address}</span></> : p.address}
                              </span>
                              <span style={{ fontSize: 9, color: S.muted, flexShrink: 0 }}>{p.market || p.layerKey}</span>
                            </div>
                          ))}
                        </>
                      )}
                      {ext.length > 0 && (
                        <>
                          <div style={{ padding: "6px 10px 3px", fontSize: 9, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 0.8, borderTop: local.length > 0 ? `1px solid ${S.border}` : "none" }}>Address Lookup</div>
                          {ext.map((r, i) => (
                            <div key={i} onClick={() => selectSuggestion(r)}
                              style={{ padding: "6px 10px", cursor: "pointer", fontSize: 11, color: S.text, borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", gap: 6 }}
                              onMouseEnter={e => e.currentTarget.style.background = S.card}
                              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                              <span style={{ color: S.red, fontSize: 10, flexShrink: 0 }}>⊕</span>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>








        </div>

        {/* Comps modal trigger — moved out of the sidebar tab row */}
        <button
          onClick={() => setCompsModalOpen(true)}
          disabled={focusedDealIds.length === 0}
          title={focusedDealIds.length === 0 ? "Focus a deal first" : "Sale, lease and rent-roll tables for the focused deals"}
          style={{ background: compsModalOpen ? S.red : S.card, border: `1px solid ${compsModalOpen ? S.red : S.border}`, color: focusedDealIds.length === 0 ? S.muted : (compsModalOpen ? "#ffffff" : S.text), borderRadius: 7, padding: "5px 10px", cursor: focusedDealIds.length === 0 ? "not-allowed" : "pointer", fontSize: 11, fontWeight: 700, fontFamily: S.font, opacity: focusedDealIds.length === 0 ? 0.5 : 1, flexShrink: 0 }}>
          Comps ↗
        </button>

        {/* Right controls */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ display: "flex", background: S.card, border: `1px solid ${S.border}`, borderRadius: 7, overflow: "hidden" }}>
            <button style={{ border: "none", background: S.red, color: "#fff", padding: "5px 10px", fontSize: 10, fontWeight: 800, fontFamily: S.font, cursor: "pointer" }}>2D</button>
            <button style={{ border: "none", background: "transparent", color: S.muted, padding: "5px 10px", fontSize: 10, fontWeight: 800, fontFamily: S.font, cursor: "pointer" }}>3D</button>
          </div>
          {/* Filters — attributes, as distinct from Location (geography) */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setFiltersOpen(o => !o)}
              style={{ background: (filtersOpen || activeFilterCount > 0) ? S.red : S.card, border: `1px solid ${(filtersOpen || activeFilterCount > 0) ? S.red : S.border}`, color: (filtersOpen || activeFilterCount > 0) ? "#ffffff" : S.text, borderRadius: 7, padding: "5px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: S.font, display: "flex", alignItems: "center", gap: 6 }}>
              &#9694; Filters
              {activeFilterCount > 0 && (
                <span style={{ background: "rgba(255,255,255,0.25)", borderRadius: 9, padding: "0 6px", fontSize: 10, fontWeight: 800 }}>
                  {activeFilterCount}
                </span>
              )}
            </button>
            {filtersOpen && (
              <MapFiltersPanel
                S={S}
                filters={attrFilters}
                onChange={setAttrFilters}
                onClose={() => setFiltersOpen(false)}
                resultCount={pinsInView.length}
                layers={layers}
                setLayers={setLayers}
                layerCounts={layerCounts}
              />
            )}
          </div>

          {mapView === "map" && (
            <button onClick={() => setCardsOpen(o => !o)}
              title={cardsOpen ? "Hide the result cards" : "Show the result cards"}
              style={{ background: cardsOpen ? S.red : S.card, border: `1px solid ${cardsOpen ? S.red : S.border}`, color: cardsOpen ? "#ffffff" : S.text, borderRadius: 7, padding: "5px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: S.font }}>
              ▦ Cards
            </button>
          )}

          {/* Map / List view switch — CoStar puts this top-right, so we do too */}
          <div style={{ display: "flex", background: S.card, border: `1px solid ${S.border}`, borderRadius: 7, overflow: "hidden" }}>
            {[["map", "◵ MAP"], ["list", "☰ LIST"]].map(([v, label]) => {
              const active = mapView === v;
              return (
                <button key={v} onClick={() => setMapView(v)}
                  title={v === "list" ? "Every pin currently shown, as a table" : "Back to the map"}
                  style={{ border: "none", background: active ? S.red : "transparent", color: active ? "#ffffff" : S.muted, padding: "5px 11px", fontSize: 10, fontWeight: 800, fontFamily: S.font, cursor: "pointer" }}>
                  {label}
                  {v === "list" && <span style={{ marginLeft: 5, fontWeight: 600, opacity: 0.8 }}>{pinsInView.length.toLocaleString()}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* ═══ MAIN LAYOUT ═══════════════════════════════════════════════════ */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* ─── MAP AREA ─────────────────────────────────────────────────── */}
        <div ref={mapAreaRef} style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          {/* Pin styling. Kept in CSS rather than inline so hover can inflate
              the pin — an inline transform cannot express :hover. */}
          <style>{`
            .pp-pin-wrap { position: relative; width: 26px; height: 26px; }
            .pp-pin {
              width: 24px; height: 24px; border-radius: 50% 50% 50% 0;
              transform: rotate(-45deg); transform-origin: center;
              display: flex; align-items: center; justify-content: center;
              border: 2px solid #ffffff;
              box-shadow: 0 2px 6px rgba(0,0,0,0.45);
              transition: transform 0.12s ease, filter 0.12s ease;
            }
            .pp-pin span {
              transform: rotate(45deg); font-size: 8px; font-weight: 900;
              font-family: 'Montserrat', sans-serif; color: #ffffff; line-height: 1;
            }
            /* Hover inflates the pin and lifts it above its neighbours. */
            .leaflet-marker-icon:hover { z-index: 10000 !important; }
            .leaflet-marker-icon:hover .pp-pin {
              transform: rotate(-45deg) scale(1.28);
              filter: brightness(1.06);
            }
            .pp-pin.muted { opacity: 0.6; }
            .pp-pin.faded { opacity: 0.3; }
            .pp-pin.assigned { width: 26px; height: 26px; }
            .pp-count {
              position: absolute; top: -5px; right: -7px;
              min-width: 16px; height: 16px; padding: 0 3px;
              border-radius: 8px; background: #ffffff; color: #333333;
              font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 900;
              display: flex; align-items: center; justify-content: center;
              border: 1px solid rgba(0,0,0,0.25); box-shadow: 0 1px 3px rgba(0,0,0,0.35);
            }
            .pp-check {
              position: absolute; top: -5px; left: -6px;
              width: 14px; height: 14px; border-radius: 50%;
              background: #4f9d76; color: #ffffff; font-size: 9px; font-weight: 900;
              display: flex; align-items: center; justify-content: center;
              border: 1.5px solid #ffffff;
            }
            .pp-cluster {
              width: 34px; height: 34px; border-radius: 50%;
              display: flex; align-items: center; justify-content: center;
              color: #ffffff; font-family: 'Montserrat', sans-serif;
              font-weight: 800; font-size: 12px;
              border: 2px solid #ffffff; box-shadow: 0 2px 8px rgba(0,0,0,0.45);
            }
          `}</style>

          {/* Map canvas */}
          <div ref={mapContainerRef} style={{ position: "absolute", inset: 0, zIndex: 0 }} />

          {/* Tool rail — consolidates zoom, polygon, radius, parcel, street
              view and basemap, which previously sat in three separate right-
              edge clusters plus two sidebar sections. */}
          {mapView === "map" && (
            <MapToolRail
              S={S}
              zoom={{
                onIn:  () => mapInstanceRef.current?.zoomIn(),
                onOut: () => mapInstanceRef.current?.zoomOut(),
              }}
              basemap={{ value: basemap, onChange: switchBasemap }}
              locationPanel={
                <MapLocationPanel
                  S={S}
                  options={locationOpts}
                  selected={locSel}
                  onChange={setLocationField}
                  onClearAll={clearAllLocation}
                />
              }
              locationCount={activeLocationCount(locSel)}
              travel={{
                ring: travelRing,
                origin: travelOrigin,
                minutes: travelMinutes,
                busy: travelBusy,
                meta: travelMeta,
                drawMode: travelDrawMode,
                estimate: estimateElements(),
                onToggleDraw: toggleTravelDrawMode,
                onMinutes: setTravelMinutes,
                onApply: runTravel,
                onClear: clearTravel,
              }}
              parcel={{ active: parcelMode, onToggle: toggleParcelMode }}
              streetView={{ active: streetViewMode, onToggle: toggleStreetView }}
              boundary={{
                ring: boundary,
                drawMode: boundaryDrawMode,
                draftCount: boundaryDraft.length,
                label: boundaryLabel,
                areaSqMi: boundary ? polygonAreaSqMi(boundary).toFixed(1) : "0",
                pinCount: pinsInView.length,
                saved: savedBoundaries,
                storeReady: boundaryStoreReady,
                nameInput: boundaryNameInput,
                saving: boundarySaving,
                onToggleDraw: toggleBoundaryDrawMode,
                onCommit: commitBoundary,
                onUndo: () => {
                  const next = boundaryDraftRef.current.slice(0, -1);
                  boundaryDraftRef.current = next;
                  setBoundaryDraft(next);
                },
                onCancelDraw: cancelBoundaryDraw,
                onClear: clearBoundary,
                onNameInput: setBoundaryNameInput,
                onSave: saveCurrentBoundary,
                onApplySaved: applySavedBoundary,
                onDeleteSaved: removeSavedBoundary,
              }}
              radius={{
                center: radiusCenter,
                miles: radiusMiles,
                input: radiusInput,
                geocoding: radiusGeocoding,
                drawMode: radiusDrawMode,
                onInput: setRadiusInput,
                onApply: applyRadiusSearch,
                onMiles: setRadiusMiles,
                onToggleDraw: toggleRadiusDrawMode,
                onClear: clearRadiusSearch,
              }}
            />
          )}

          {/* Loading overlay */}
          {loading && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}>
              <div style={{ background: S.sidebar, borderRadius: 10, padding: "18px 30px", fontSize: 13, fontWeight: 700, color: S.text }}>
                Loading pipeline data…
              </div>
            </div>
          )}

          {/* Result count + comps drawer trigger + deal-focus chips. Offset to
              the right of the tool rail, which occupies x = 10..56. */}
          <div style={{ position: "absolute", top: 10, left: 66, zIndex: 500, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", maxWidth: "calc(100% - 180px)" }}>
            {!presentMode && (
              <div style={{ background: S.chipBg, borderRadius: 6, padding: "3px 9px", fontSize: 10, fontWeight: 700, color: S.muted, backdropFilter: "blur(4px)" }}>
                {pipelineVisible + dbVisible} visible of {properties.length} total
              </div>
            )}

            {/* Which focused deal the rent roll is scoped to. Lived in
                the sidebar until the sidebar was retired. */}
            {focusedDealIds.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 5, background: S.chipBg, backdropFilter: "blur(4px)", border: `1px solid ${S.border}`, borderRadius: 6, padding: "3px 8px" }}>
              <span style={{ fontSize: 9, color: S.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, alignSelf: "center" }}>Show:</span>
              {focusedDealIds.map((did, idx) => {
                const color = DEAL_FOCUS_COLORS[idx] || "#888";
                const name = focusedDealsData.get(did)?.name || "deal";
                const active = drawerDealId === did;
                return (
                  <button
                    key={did}
                    onClick={() => setDrawerDealId(did)}
                    style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, border: `1px solid ${active ? color : S.border}`, background: active ? `${color}1f` : "transparent", cursor: "pointer", fontFamily: S.font }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: active ? color : S.text, whiteSpace: "nowrap" }}>{name}</span>
                  </button>
                );
              })}
              </div>
            )}
            {focusedDealIds.length > 0 && (
              <>
                {/* One chip per focused deal, colored by slot */}
                {focusedDealIds.map((did, idx) => {
                  const color = DEAL_FOCUS_COLORS[idx] || "#888";
                  const data = focusedDealsData.get(did);
                  const name = data?.name || properties.find(p => p.isPipeline && p.dealId === did)?._dealName || properties.find(p => p.isPipeline && p.dealId === did)?.address?.split(",")[0] || "deal";
                  const compCount = data?.comps?.length || 0;
                  const rrCount = data?.rentRoll?.length || 0;
                  return (
                    <div key={did} style={{ display: "flex", alignItems: "center", gap: 5, background: `${color}1f`, border: `1px solid ${color}`, borderRadius: 6, padding: "3px 4px 3px 8px", backdropFilter: "blur(4px)" }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                      <span style={{ fontSize: 10, fontWeight: 800, color, fontFamily: S.font, letterSpacing: 0.2, whiteSpace: "nowrap" }}>{name}</span>
                      <span style={{ fontSize: 9, color, opacity: 0.7, fontFamily: S.font, whiteSpace: "nowrap" }}>· {compCount} · {rrCount} RR</span>
                      <button
                        onClick={() => onChangeFocusedDealIds?.(focusedDealIds.filter(x => x !== did))}
                        style={{ background: "transparent", border: "none", color, cursor: "pointer", fontSize: 13, padding: "0 5px", lineHeight: 1, fontWeight: 700 }}
                        title={`Remove ${name} from focus`}
                      >✕</button>
                    </div>
                  );
                })}
                {/* + Add deal */}
                {focusedDealIds.length < MAX_FOCUSED_DEALS && (
                  <button
                    onClick={() => setAddDealPickerOpen(true)}
                    style={{ background: S.chipBg, border: `1px dashed ${S.border}`, borderRadius: 6, padding: "3px 9px", fontSize: 10, fontWeight: 800, color: S.text, fontFamily: S.font, cursor: "pointer", backdropFilter: "blur(4px)", whiteSpace: "nowrap" }}
                    title="Add another deal to compare on the map"
                  >
                    + Add deal
                  </button>
                )}
                {focusedDealIds.length >= MAX_FOCUSED_DEALS && (
                  <button
                    onClick={() => setAddDealPickerOpen(true)}
                    style={{ background: S.chipBg, border: `1px solid ${S.border}`, borderRadius: 6, padding: "3px 9px", fontSize: 10, fontWeight: 800, color: S.muted, fontFamily: S.font, cursor: "pointer", backdropFilter: "blur(4px)", whiteSpace: "nowrap" }}
                    title="Cap is 3 deals — adding will replace one"
                  >
                    Swap deal…
                  </button>
                )}
                {/* Show-nearby / only-assigned toggle (per D4) */}
                <button
                  onClick={() => setFocusMode(focusMode === "with_nearby" ? "only_assigned" : "with_nearby")}
                  style={{ background: S.chipBg, border: `1px solid ${S.border}`, borderRadius: 6, padding: "3px 9px", fontSize: 10, fontWeight: 800, color: S.text, fontFamily: S.font, cursor: "pointer", backdropFilter: "blur(4px)", whiteSpace: "nowrap" }}
                  title="Toggle whether faded nearby comps render alongside the deal's assigned comps"
                >
                  {focusMode === "with_nearby" ? "👁  Assigned + Nearby" : "🔍 Only Assigned"}
                </button>
                {/* Bulk selection mode toggle */}
                <button
                  onClick={() => { const next = !selectionMode; setSelectionMode(next); selectionModeRef.current = next; if (!next) setSelectedCompIds(new Set()); }}
                  style={{ background: selectionMode ? "rgba(79,157,118,0.18)" : S.chipBg, border: `1px solid ${selectionMode ? "#4f9d76" : S.border}`, borderRadius: 6, padding: "3px 9px", fontSize: 10, fontWeight: 800, color: selectionMode ? "#4f9d76" : S.text, fontFamily: S.font, cursor: "pointer", backdropFilter: "blur(4px)", whiteSpace: "nowrap" }}
                  title="Click comp pins to add or remove them from the deal. Cmd/Ctrl+click works without entering this mode."
                >
                  {selectionMode ? "☑ Selecting…" : "☐ Select Multiple"}
                </button>
                {/* Present mode toggle (Phase 5.1) */}
                <button
                  onClick={() => setPresentMode(p => !p)}
                  style={{ background: presentMode ? "rgba(233,53,41,0.2)" : S.chipBg, border: `1px solid ${presentMode ? S.red : S.border}`, borderRadius: 6, padding: "3px 9px", fontSize: 10, fontWeight: 800, color: presentMode ? S.red : S.text, fontFamily: S.font, cursor: "pointer", backdropFilter: "blur(4px)", whiteSpace: "nowrap" }}
                  title="Hide chrome and show a full-bleed map with a deal-summary overlay for IC meetings"
                >
                  🎬 {presentMode ? "Exit Present" : "Present"}
                </button>
                {/* Snapshot to PNG (Phase 5.2) */}
                <button
                  onClick={handleSnapshot}
                  disabled={snapshotBusy}
                  style={{ background: S.chipBg, border: `1px solid ${S.border}`, borderRadius: 6, padding: "3px 9px", fontSize: 10, fontWeight: 800, color: S.text, fontFamily: S.font, cursor: snapshotBusy ? "wait" : "pointer", backdropFilter: "blur(4px)", whiteSpace: "nowrap", opacity: snapshotBusy ? 0.6 : 1 }}
                  title="Capture the current map view (with overlays) and copy to clipboard for slide decks"
                >
                  {snapshotBusy ? "📷 Capturing…" : "📷 Snapshot"}
                </button>
              </>
            )}
          </div>

          {/* Present-mode Exit button (top-right) — replaces the Street/Parcel
              buttons. Sized + colored so it's hard to miss under stage lights. */}
          {presentMode && (
            <button
              onClick={() => setPresentMode(false)}
              style={{ position: "absolute", top: 10, right: 10, zIndex: 850, background: S.red, color: "#fff", border: "2px solid #fff", borderRadius: 8, padding: "6px 14px", fontSize: 11, fontWeight: 800, fontFamily: S.font, cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.4)", whiteSpace: "nowrap" }}
              title="Exit Present mode"
            >
              🎬 Exit Present
            </button>
          )}

          {/* Deal Summary overlay (Present mode, top-right under Exit btn).
              Per D8=A: shows the drawer-active deal only. Card scales to deal
              stats so audiences can see headline numbers from across the room. */}
          {presentMode && (() => {
            const activeDealId = drawerDealId || focusedDealIds[0];
            if (!activeDealId) return null;
            const data = focusedDealsData.get(activeDealId);
            if (!data) return null;
            const slotIdx = focusedDealIds.indexOf(activeDealId);
            const accent = DEAL_FOCUS_COLORS[slotIdx] || S.red;
            const dealPin = properties.find(p => p.isPipeline && p.dealId === activeDealId);
            const sales = (data.comps || []).filter(c => c.type === "sale");
            const leases = (data.comps || []).filter(c => c.type === "lease" && c.source !== "rent_roll");
            const avgSalePsf = avgOf(sales.map(c => Number(c.price_psf)));
            const avgSaleCap = avgOf(sales.map(c => Number(c.cap_rate)));
            const avgLeaseRate = avgOf(leases.map(c => Number(c.lease_rate)));
            const rrTotalSf = (data.rentRoll || []).reduce((s, r) => s + (Number(r.suite_sf) || 0), 0);
            const rrWeighted = (data.rentRoll || []).reduce((s, r) => s + (Number(r.suite_sf) || 0) * (Number(r.lease_rate) || 0), 0);
            const avgInPlace = rrTotalSf > 0 ? rrWeighted / rrTotalSf : null;
            const avgWalt = avgOf((data.rentRoll || []).map(r => Number(r.walt_years)).filter(n => n > 0));
            const mtm = (avgLeaseRate && avgInPlace) ? ((avgLeaseRate / avgInPlace) - 1) * 100 : null;
            return (
              <div style={{ position: "absolute", top: 60, right: 16, zIndex: 700, width: 340, background: S.overlayCard, color: S.text, border: `2px solid ${accent}`, borderRadius: 12, padding: "14px 16px", fontFamily: S.font, backdropFilter: "blur(8px)", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: accent }} />
                  <span style={{ fontSize: 9, fontWeight: 800, color: accent, textTransform: "uppercase", letterSpacing: 1 }}>Deal Summary</span>
                </div>
                <div style={{ fontSize: 17, fontWeight: 900, lineHeight: 1.15, marginBottom: 2 }}>{data.name}</div>
                <div style={{ fontSize: 10, color: S.muted, marginBottom: 10 }}>{dealPin?.market || ""}{dealPin?.stage ? ` · ${dealPin.stage}` : ""}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11 }}>
                  {dealPin?.askingPrice ? <KV k="Ask" v={fmtBig(dealPin.askingPrice)} S={S} /> : null}
                  {dealPin?.underwrittenPrice ? <KV k="UW Price" v={fmtBig(dealPin.underwrittenPrice)} accent S={S} /> : null}
                  <KV k="Sale Comps" v={`${sales.length}${avgSalePsf ? ` · $${avgSalePsf.toFixed(0)}/SF` : ""}`} S={S} />
                  <KV k="Lease Comps" v={`${leases.length}${avgLeaseRate ? ` · $${avgLeaseRate.toFixed(2)}/SF` : ""}`} S={S} />
                  <KV k="Avg Cap" v={avgSaleCap ? `${avgSaleCap.toFixed(2)}%` : "—"} S={S} />
                  <KV k="Rent Roll" v={`${data.rentRoll?.length || 0} · ${avgInPlace ? `$${avgInPlace.toFixed(2)}/SF` : "—"}`} S={S} />
                  {avgWalt ? <KV k="WALT" v={`${avgWalt.toFixed(1)}y`} S={S} /> : null}
                  {mtm != null ? (
                    <div style={{ gridColumn: "1 / -1", marginTop: 6, paddingTop: 8, borderTop: `1px solid ${S.border}`, fontSize: 12, fontWeight: 800 }}>
                      Mark-to-Market: <span style={{ color: mtm > 0 ? "#4f9d76" : mtm < 0 ? accent : S.text }}>{mtm > 0 ? "+" : ""}{mtm.toFixed(1)}%</span>
                    </div>
                  ) : null}
                </div>
                {focusedDealIds.length > 1 && (
                  <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${S.border}`, display: "flex", gap: 4, flexWrap: "wrap", fontSize: 9, color: S.muted }}>
                    <span style={{ alignSelf: "center", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Switch:</span>
                    {focusedDealIds.map((did, i) => {
                      const c = DEAL_FOCUS_COLORS[i];
                      const n = focusedDealsData.get(did)?.name || "deal";
                      const active = did === activeDealId;
                      return (
                        <button key={did} onClick={() => setDrawerDealId(did)}
                          style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 6px", borderRadius: 4, border: `1px solid ${active ? c : S.border}`, background: active ? `${c}1f` : "transparent", cursor: "pointer", color: active ? c : S.text, fontFamily: S.font, fontSize: 9, fontWeight: 700 }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: c }} />
                          {n}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}






          {/* Rent roll card (📋 pin click). Floats over the map bottom-left,
              clear of the tool rail; was a sidebar panel before Phase 8. */}
          {rentRollPanelAddress && (() => {
            // Scope to the drawer-active deal if there are multiple (so a
            // shared subject address shows only one deal's rent roll at a time)
            const rows = focusedRentRoll.filter(r =>
              (r.subject_address || "").trim() === rentRollPanelAddress &&
              (!drawerDealId || r._dealId === drawerDealId)
            );
            const totalSf = rows.reduce((s, r) => s + (Number(r.suite_sf) || 0), 0);
            const weighted = rows.reduce((s, r) => s + (Number(r.suite_sf) || 0) * (Number(r.lease_rate) || 0), 0);
            const avgRate = totalSf > 0 ? weighted / totalSf : 0;
            const walts = rows.map(r => Number(r.walt_years)).filter(n => Number.isFinite(n) && n > 0);
            const avgWalt = walts.length > 0 ? walts.reduce((s, n) => s + n, 0) / walts.length : null;
            return (
              <div style={{ position: "absolute", bottom: 28, left: 66, zIndex: 600, width: 340, maxHeight: 420, overflowY: "auto", padding: 12, background: S.overlayCard, backdropFilter: "blur(12px)", border: `1px solid ${S.border}`, borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.6)", fontFamily: "'Montserrat', sans-serif" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                      <span style={{ fontSize: 15 }}>📋</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: S.text }}>Rent Roll</span>
                    </div>
                    <div style={{ fontSize: 10, color: S.muted, marginLeft: 22 }}>{rentRollPanelAddress}</div>
                  </div>
                  <button onClick={() => setRentRollPanelAddress(null)} style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: 17, lineHeight: 1, padding: 4 }}>✕</button>
                </div>
                {/* Summary */}
                <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                  <SummaryStat label="Tenants" value={rows.length} S={S} />
                  <SummaryStat label="Total SF" value={totalSf.toLocaleString()} S={S} />
                  {avgRate > 0 && <SummaryStat label="Avg In-Place" value={`$${avgRate.toFixed(2)}/SF`} S={S} accent />}
                  {avgWalt && <SummaryStat label="Avg WALT" value={`${avgWalt.toFixed(1)}y`} S={S} />}
                </div>
                {/* Tenant table */}
                <div style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1.4fr 0.7fr 0.7fr 0.8fr 0.8fr", padding: "6px 10px", borderBottom: `1px solid ${S.border}`, fontSize: 8, fontWeight: 800, color: S.muted, textTransform: "uppercase", letterSpacing: 0.5, background: S.card2 }}>
                    <span>Tenant</span><span>Suite</span><span>SF</span><span>Rate</span><span>Term</span>
                  </div>
                  {rows.map((r, i) => (
                    <div key={r.id || i} style={{ display: "grid", gridTemplateColumns: "1.4fr 0.7fr 0.7fr 0.8fr 0.8fr", padding: "6px 10px", borderBottom: i < rows.length - 1 ? `1px solid ${S.border}` : "none", fontSize: 10, color: S.text, alignItems: "center" }}>
                      <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.tenant || "—"}</span>
                      <span style={{ color: S.muted }}>{r.suite_id || "—"}</span>
                      <span>{r.suite_sf ? Number(r.suite_sf).toLocaleString() : "—"}</span>
                      <span>{r.lease_rate ? `$${Number(r.lease_rate).toFixed(2)}` : "—"}</span>
                      <span style={{ fontSize: 9, color: S.muted }}>{r.lease_end || (r.walt_years ? `${Number(r.walt_years).toFixed(1)}y` : "—")}</span>
                    </div>
                  ))}
                  {rows.length === 0 && (
                    <div style={{ padding: 12, textAlign: "center", color: S.muted, fontSize: 10 }}>No rent roll rows at this address.</div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── Property Details panel ──────────────────────────────────── */}

          {/* Parcel info card (bottom right, above Leaflet attribution) */}
          {parcelInfo && (
            <div style={{ position: "absolute", bottom: 28, right: 10, zIndex: 600, width: 300, maxHeight: 360, background: S.overlayCard, backdropFilter: "blur(12px)", border: `1px solid ${S.border}`, borderRadius: 10, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.6)", transition: "bottom 0.3s" }}>
              {/* Header */}
              <div style={{ padding: "10px 12px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#e93529" }} />
                  <span style={{ fontSize: 11, fontWeight: 800, color: S.text }}>Parcel Info</span>
                  <span style={{ fontSize: 9, fontWeight: 600, color: S.muted }}>{parcelInfo.county}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    onClick={handleAddParcelToSourcing}
                    disabled={addingSourcing}
                    style={{ background: "#e93529", border: "none", borderRadius: 5, color: "#fff", padding: "3px 10px", fontSize: 9, fontWeight: 700, fontFamily: "'Montserrat', sans-serif", cursor: addingSourcing ? "not-allowed" : "pointer", opacity: addingSourcing ? 0.5 : 1, whiteSpace: "nowrap", transition: "opacity 0.15s" }}
                  >
                    {addingSourcing ? "Adding…" : "+ Add to Sourcing"}
                  </button>
                  <button onClick={() => { setParcelInfo(null); const map = mapInstanceRef.current; if (parcelLayerRef.current && map) { map.removeLayer(parcelLayerRef.current); parcelLayerRef.current = null; } }}
                    style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: 15, lineHeight: 1, padding: 0 }}>✕</button>
                </div>
              </div>
              {/* Fields */}
              <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px", scrollbarWidth: "thin", scrollbarColor: "#333 transparent" }}>
                {[
                  ["Owner", parcelInfo.owner],
                  ["Address", parcelInfo.address],
                  ["City", parcelInfo.city],
                  ["Use", parcelInfo.use],
                  ["Zoning", parcelInfo.zoning],
                  ["Total Value", parcelInfo.totalValue ? "$" + Number(parcelInfo.totalValue).toLocaleString() : null],
                  ["Land Value", parcelInfo.landValue ? "$" + Number(parcelInfo.landValue).toLocaleString() : null],
                  ["Improvement", parcelInfo.improvement ? "$" + Number(parcelInfo.improvement).toLocaleString() : null],
                  ["Last Sale Date", parcelInfo.lastSaleDate],
                  ["Last Sale Price", parcelInfo.lastSale],
                  ["Land Sq Ft", parcelInfo.sqft ? Number(parcelInfo.sqft).toLocaleString() : null],
                  ["Building Sq Ft", parcelInfo.buildingSqft ? Number(parcelInfo.buildingSqft).toLocaleString() : null],
                  ["Year Built", parcelInfo.yearBuilt],
                  ["Floors", parcelInfo.floors],
                  ["Units", parcelInfo.units],
                  ["Acreage", parcelInfo.acreage],
                ].filter(([, v]) => v != null && v !== "" && v !== 0).map(([label, value]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 7, gap: 8 }}>
                    <span style={{ fontSize: 9, color: S.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, flexShrink: 0 }}>{label}</span>
                    <span style={{ fontSize: 11, color: S.text, fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}


          {/* Geocoding progress */}
          {!loading && geocoding.total > 0 && geocoding.done < geocoding.total && (
            <div style={{ position: "absolute", bottom: 50, left: "50%", transform: "translateX(-50%)", background: S.overlayCard, color: "#ccc", padding: "6px 14px", borderRadius: 16, fontSize: 10, fontWeight: 700, zIndex: 500, whiteSpace: "nowrap", backdropFilter: "blur(4px)", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 180 }}>
              <span>Placing portfolio addresses… {geocoding.done}/{geocoding.total}</span>
              <div style={{ width: "100%", height: 4, borderRadius: 2, background: "#333", overflow: "hidden" }}>
                <div style={{ width: `${(geocoding.done / geocoding.total * 100)}%`, height: "100%", background: S.red, borderRadius: 2, transition: "width 0.3s" }} />
              </div>
            </div>
          )}

          {/* Street View panel */}
          {streetViewCoords && (
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "45%", zIndex: 700, background: "#000", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 12px", background: S.sidebar, borderTop: `2px solid ${S.red}`, flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: S.text }}>🚶 Street View</span>
                  <span style={{ fontSize: 10, color: S.muted }}>{streetViewCoords.lat.toFixed(5)}, {streetViewCoords.lng.toFixed(5)}</span>
                </div>
                <button onClick={() => { setStreetViewCoords(null); streetViewPanoRef.current = null; }}
                  style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "2px 6px" }}>✕</button>
              </div>
              <div ref={streetViewRef} style={{ flex: 1 }} />
            </div>
          )}

          {/* ─── Comp Tables fullscreen modal (Comps button in sidebar) ──── */}
          {compsModalOpen && (
            <div
              onClick={() => setCompsModalOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "stretch", justifyContent: "center", padding: 24, fontFamily: S.font }}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{ flex: 1, maxWidth: 1600, background: S.bg || S.sidebar, color: S.text, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", overflow: "hidden", border: `1px solid ${S.border}` }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: `1px solid ${S.border}`, background: S.card2 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 16 }}>📊</span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: S.text, letterSpacing: 0.3 }}>Deal Comp Tables</span>
                    <span style={{ fontSize: 11, color: S.muted, fontWeight: 600 }}>
                      {focusedDealIds.length === 1
                        ? focusedDealsData.get(focusedDealIds[0])?.name || ""
                        : `${focusedDealIds.length} deals focused`}
                    </span>
                  </div>
                  <button
                    onClick={() => setCompsModalOpen(false)}
                    style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 4 }}
                    title="Close (Esc)"
                  >✕</button>
                </div>
                <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>
                  <FocusedDealCompTables
                    focusedDealIds={focusedDealIds}
                    focusedDealsData={focusedDealsData}
                    setFocusedDealsData={setFocusedDealsData}
                    drawerDealId={drawerDealId}
                    setDrawerDealId={setDrawerDealId}
                    S={S}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Result cards beside the map. Hidden in Present mode and while
              the list or a property page is up. */}
          {mapView === "map" && cardsOpen && !presentMode && !propertyAddrKey && (
            <MapResultCards
              pins={pinsInView}
              S={S}
              onOpenPin={(prop) => openPropertyPage(prop)}
              onClose={() => setCardsOpen(false)}
            />
          )}

          {/* Property page. Full-cover layer, same reasoning as the list. */}
          {propertyAddress && (
            <PropertyPage
              address={propertyAddress}
              records={propertyRecords}
              comps={rawComps}
              S={S}
              index={propertyPageIndex}
              total={addressList.length}
              onPrev={() => stepPropertyPage(-1)}
              onNext={() => stepPropertyPage(1)}
              onClose={() => setPropertyAddrKey(null)}
              onShowOnMap={(prop) => { setPropertyAddrKey(null); setMapView("map"); focusPinFromTable(prop); }}
              onOpenDeal={(dealId) => onNavigateToDeal?.(dealId)}
            />
          )}

          {/* List view. Rendered as a full-cover layer rather than swapping the
              map out of the DOM — unmounting the map container would destroy
              the Leaflet instance, which is created once on mount. */}
          {mapView === "list" && (
            <MapListView
              pins={pinsInView}
              comps={rawComps}
              S={S}
              boundaryLabel={boundary ? (boundaryLabel || "custom boundary") : ""}
              selectedCompIds={selectedCompIds}
              setSelectedCompIds={setSelectedCompIds}
              onFocusPin={(prop) => openPropertyPage(prop)}
              onOpenDeal={(dealId) => onNavigateToDeal?.(dealId)}
              onClose={() => setMapView("map")}
            />
          )}

          {/* + Add deal picker modal (Phase 4 multi-deal) */}
          <AddDealPicker
            open={addDealPickerOpen}
            onClose={() => setAddDealPickerOpen(false)}
            focusedDealIds={focusedDealIds}
            availableDeals={availableDeals}
            focusedDealsData={focusedDealsData}
            S={S}
            darkMode={darkMode}
            onAdd={(newDealId, replaceTargetId) => {
              const next = [...focusedDealIds];
              if (replaceTargetId) {
                const idx = next.indexOf(replaceTargetId);
                if (idx >= 0) next[idx] = newDealId;
                else next.push(newDealId);
              } else {
                next.push(newDealId);
              }
              onChangeFocusedDealIds?.(next.slice(0, MAX_FOCUSED_DEALS));
              setAddDealPickerOpen(false);
            }}
          />

          {/* ─── Comps Drawer (CoStar-style) ─────────────────────────────────
              Slides in from the right when the user clicks "Comps List". Shows
              every comp that passes the active layer toggles + market filter
              + search + radius. Each row flies the map to the comp; the header
              has an XLSX export button for the visible set. */}
          {focusedDealIds.length > 0 && selectedCompIds.size > 0 && (() => {
            const targetDealId = bulkApplyDealId && focusedDealIds.includes(bulkApplyDealId) ? bulkApplyDealId : focusedDealIds[0];
            const targetAssignedIds = new Set((focusedDealsData.get(targetDealId)?.comps || []).map(c => c.id));
            const toAddIds = [...selectedCompIds].filter(id => !targetAssignedIds.has(id));
            const toRemoveIds = [...selectedCompIds].filter(id => targetAssignedIds.has(id));
            const targetData = focusedDealsData.get(targetDealId);
            const targetName = targetData?.name || "this deal";
            const targetSlot = focusedDealIds.indexOf(targetDealId);
            const targetColor = DEAL_FOCUS_COLORS[targetSlot] || "#e93529";
            return (
              <div style={{ position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 850, display: "flex", alignItems: "center", gap: 10, background: S.overlayCard, border: `1px solid ${S.border}`, borderRadius: 10, padding: "8px 14px", boxShadow: "0 8px 24px rgba(0,0,0,0.4)", backdropFilter: "blur(8px)", whiteSpace: "nowrap" }}>
                {focusedDealIds.length > 1 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 10, color: S.muted, fontFamily: S.font }}>Apply to:</span>
                    <select
                      value={targetDealId || ""}
                      onChange={e => setBulkApplyDealId(e.target.value)}
                      style={{ background: S.card, border: `1px solid ${targetColor}`, borderRadius: 5, padding: "3px 6px", fontSize: 11, fontWeight: 700, color: S.text, fontFamily: S.font, cursor: "pointer" }}
                    >
                      {focusedDealIds.map((did, idx) => {
                        const d = focusedDealsData.get(did);
                        return <option key={did} value={did}>{d?.name || "deal"} ({DEAL_FOCUS_COLORS[idx] === "#e93529" ? "red" : DEAL_FOCUS_COLORS[idx] === "#8a8a8a" ? "blue" : "green"})</option>;
                      })}
                    </select>
                  </div>
                )}
                <span style={{ fontSize: 11, fontWeight: 700, color: S.text, fontFamily: S.font }}>
                  {toAddIds.length > 0 && <span style={{ color: "#4f9d76" }}>{toAddIds.length} to add</span>}
                  {toAddIds.length > 0 && toRemoveIds.length > 0 && <span style={{ color: S.muted }}> · </span>}
                  {toRemoveIds.length > 0 && <span style={{ color: S.red }}>{toRemoveIds.length} to remove</span>}
                </span>
                <button
                  onClick={async () => {
                    let added = 0, removed = 0;
                    if (toAddIds.length > 0) {
                      const result = await assignCompsToDeal(targetDealId, toAddIds, "map_assign");
                      added = result.count;
                      if (result.error) window.alert(`Couldn't assign ${toAddIds.length} comp(s): ${result.error}`);
                    }
                    if (toRemoveIds.length > 0) {
                      for (const id of toRemoveIds) {
                        await unassignCompFromDeal(targetDealId, id);
                        removed++;
                      }
                    }
                    // Refresh the target deal's data in focusedDealsData
                    const fresh = await loadAssignedComps(targetDealId);
                    setFocusedDealsData(prev => {
                      const next = new Map(prev);
                      const existing = next.get(targetDealId) || {};
                      next.set(targetDealId, { ...existing, comps: fresh || [] });
                      return next;
                    });
                    const parts = [];
                    if (added > 0) parts.push(`added ${added}`);
                    if (removed > 0) parts.push(`removed ${removed}`);
                    showToast(`✓ ${parts.join(", ")} comp${added + removed === 1 ? "" : "s"} ${added > 0 && removed > 0 ? "in" : (added > 0 ? "to" : "from")} ${targetName}`, 4000);
                    setSelectedCompIds(new Set());
                    setSelectionMode(false);
                    selectionModeRef.current = false;
                  }}
                  style={{ background: targetColor, border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 11, fontWeight: 800, color: "#fff", fontFamily: S.font, cursor: "pointer" }}
                >
                  Apply
                </button>
                <button
                  onClick={() => setSelectedCompIds(new Set())}
                  style={{ background: "transparent", border: `1px solid ${S.border}`, borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 700, color: S.muted, fontFamily: S.font, cursor: "pointer" }}
                >
                  Clear
                </button>
              </div>
            );
          })()}

          {/* Toast notification */}
          {toast && (
            <div style={{ position: "absolute", bottom: 80, right: 16, zIndex: 2000, background: S.card, border: `1px solid ${S.border}`, borderRadius: 8, padding: "8px 14px", fontSize: 11, fontWeight: 600, color: S.text, boxShadow: "0 4px 12px rgba(0,0,0,0.4)", maxWidth: 260, animation: "none" }}>
              {toast}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── App Root ─────────────────────────────────────────────────────────────────


export default MapView;
